/**
 * # dxfBridge.export
 *
 * Top-level Drawing → DXF orchestrators plus the Drawing → DXF-side
 * style-table encoders and raw-passthrough helpers.
 *
 * This module drives the write path of the bridge:
 *
 *   exportDrawingDocumentToDxfString(doc, opts?)
 *     -> exportDrawingDocumentToDxfDocument(doc)
 *         -> exportStyleTables(styleTables, existingTables, blockRecords)
 *            -> drawingTextStyleToDxfEntry / drawingDimStyleToDxfEntry /
 *               exportLayers
 *         -> toDxfEntity(entity, space)       [./dxfBridge.entityConversion]
 *         -> buildBlocksSectionFromDrawingBlocks(document)
 *            -> toDxfEntity, rawEntityGroupToDxfUnsupported
 *         -> exportBlockRecords / exportLayouts / exportDynamicBlockObjects
 *         -> rawEntityGroupToDxfUnsupported(rawEntityGroup)
 *     -> serializeDxfDocument                 [@dxf-ai-editor/dxf-io]
 *
 * drawingMLeaderStyleToDxfMLeaderStyle is the inverse mapping used by the
 * DXF writer when the OBJECTS section emits MLEADERSTYLE entries; it lives
 * here because it belongs with the Drawing → DXF style encoders even though
 * the orchestrator that invokes it is the writer stub inside dxf-io.
 *
 * Split from the monolithic `dxfBridge.ts` to respect the CLAUDE.md
 * 2000-line guardrail. All public entry points remain re-exported from
 * `dxfBridge.ts` so consumers don't need to update their imports.
 */

import {
  patchDocumentVarsIntoHeaderGroups,
  patchDynVarsIntoHeaderGroups,
  patchLinetypeVarsIntoHeaderGroups,
  patchPointVarsIntoHeaderGroups,
  patchSnapVarsIntoHeaderGroups,
  serializeDxfDocument,
  type DxfBlockRecordEntry,
  type DxfDimensionStyleEntry,
  type DxfDocument,
  type DxfEntity,
  type DxfGroup,
  type DxfLayerEntry,
  type DxfMLeaderEntity,
  type DxfObjectsSection,
  type DxfSection,
  type DxfTablesSection,
  type DynVarHeaderKey,
  type HeaderDocumentVars,
  type HeaderLinetypeVars,
  type HeaderPointVars,
  type HeaderSnapVars,
} from "@dxf-ai-editor/dxf-io";

import { toDxfEntity } from "./dxfBridge.entityConversion";
import { applyAssocArrayWriteBack } from "./dxfBridge.arrayAssocExport";
import {
  collectAllEmittedHandles,
  maxHexHandleValue,
  patchHeaderHandSeedToMax,
} from "./dxfBridge.handleSeed";
import {
  buildAnnotativeObjectsGraph,
  collectAnnotativeGraphMintedHandles,
  collectOwnHandlesDeep,
  makeHandleMinter,
  stripPreservedScaleListDict,
} from "./dxfBridge.annotativeExport";
import {
  ensureConformantSkeleton,
  isImportedSourceDocument,
} from "./dxfBridge.conformantSkeleton";
import type { CarriedObjectRecord } from "./dxfBridge.carriedObjectRecords";
import { buildPaperSpaceLayoutPlan } from "./dxfBridge.paperSpaceLayout";
// SL-5 P2-1 — MLEADERSTYLE object emission + entity-340 stamp + dict wiring were
// extracted to their own module to keep this orchestrator under the 2000-line
// cap. `drawingMLeaderStyleToDxfMLeaderStyle` is re-exported below so the public
// barrel surface is unchanged.
import {
  buildReferencedMLeaderStyleObjects,
  collectForeignObjectRecordHandles,
  findObjectDictionaryHandle,
  isDefaultStandardStyle,
  maybeSynthesizeMLeaderStyleRootDicts,
  replaceDictionaryChildEntries,
  stampMLeaderLeaderStyleHandles,
  stampMLeaderStyleReferencedRecordHandles,
} from "./dxfBridge.mleaderStyleObjects";
// Re-export so `export * from "./dxfBridge.export"` (the barrel) keeps exposing
// `drawingMLeaderStyleToDxfMLeaderStyle` from its historical path.
export { drawingMLeaderStyleToDxfMLeaderStyle } from "./dxfBridge.mleaderStyleObjects";
// #314 round-2 — the BLOCKS-section builder was extracted to its own module to
// keep this file under the 2000-line cap. Imported for internal use (the
// orchestrator + the ENTITIES raw-group passthrough) AND re-exported at the
// bottom so the public barrel surface is unchanged.
import {
  buildBlocksSectionFromDrawingBlocks,
  rawEntityGroupToDxfUnsupported,
} from "./dxfBridge.blocksSection";
import { exportBlockRecords } from "./dxfBridge.blockRecords";
import { stampMLeaderTextStyleHandles } from "./dxfBridge.mleaderTextStyleStamp";
import { prepareImportedDocumentForExport } from "./dxfBridge.newRecordHandles";
import { stampDimensionDStyleArrowheadHandles, stampMleaderEntityArrowheadHandles } from "./dxfBridge.dimStyleArrowhead";
import { exportLayouts } from "./dxfBridge.layouts";
import { exportLinetypes } from "./dxfBridge.linetypes";
import {
  collectRetainedDynamicBlockHandles,
  exportDynamicBlockObjects,
} from "./dxfBridge.dynamic";
import { applyViewportClipBoundarySynthesis } from "./viewportClipObjectsSynthesize";

import { cecolorToHeader } from "./color";

import {
  type DimensionStyle,
  type DrawingDocument,
  type DrawingEntity,
  type LayerDefinition,
  type Linetype,
  type RawEntityGroup,
  type StyleTables,
} from "./model";

export type ExportDrawingDocumentToDxfStringOptions = {
  newline?: string;
  /**
   * DXF HEADER persistence for Dynamic Input system variables.
   * When provided, the HEADER raw-passthrough section is patched to include
   * (or update) the $DYN* and $TEMPOVERRIDES variable entries so they are
   * preserved across load/save round-trips (AutoCAD parity — Phase 4).
   */
  dynVars?: Partial<Record<DynVarHeaderKey, number>>;
  /**
   * Phase 4 Wave 1 Task C — pre-built per-space entity snapshots.
   *
   * When the caller routes the document through the compact-cache
   * read-through wrapper (`spaceReadThrough.wrapSpaceAsReadThrough`), the
   * `space.entities` getter materialises a fresh array on every access.
   * If the cache is being mutated concurrently (e.g. AI drafting tool
   * appending entities during a long-running serialise), the writer
   * could observe a partially mutated state.
   *
   * Callers that need a stable snapshot can pre-compute one via
   * `snapshotSpaceEntities(docId, spaceId)` for every space they intend
   * to export, populate this map keyed by `space.id`, and the exporter
   * will iterate the supplied array instead of reading `space.entities`.
   *
   * **Identity contract:** the exporter does not mutate the supplied
   * arrays. Entity objects inside come from the cache materialiser and
   * follow the "freshly constructed" identity contract — callers should
   * not retain them past the export call.
   *
   * Absent / undefined falls through to `space.entities` (the legacy
   * direct iteration path), so this remains backwards-compatible with
   * all existing callers.
   */
  entitiesSnapshot?: Map<string, DrawingEntity[]>;
  /**
   * DXF-CONFORMANT-SKELETON — when `true`, a FROM-SCRATCH document (one that
   * carries no verbatim raw source: `rawPassthroughSections === undefined` and
   * empty `rawTableGroups` / `rawObjectGroups`) is back-filled with the
   * conformant blank DXF skeleton (HEADER `$ACADVER`/`$HANDSEED`, CLASSES,
   * VPORT/APPID/VIEW/UCS tables, BLOCK_RECORD `*Model_Space`/`*Paper_Space`,
   * BLOCKS, OBJECTS root DICTIONARY) and every entity handle is re-minted to
   * hex with owner BLOCK_RECORD wiring, so the export is AutoCAD/ezdxf-readable.
   *
   * Defaults to `false` — the raw library export stays byte-stable for the
   * hundreds of in-memory round-trip FIXTURE tests that construct a doc with no
   * raw source and re-import the output (re-minting handles + injecting the
   * skeleton would break their handle-reference assertions). The genuine
   * user-facing SAVE / download / copy / preview paths (App.tsx +
   * editorStore.exportDxfText) pass `true` so a File → New drawing saves as a
   * conformant DXF. Imported documents are returned unchanged regardless of this
   * flag (their byte-faithful carry-forward is preserved).
   */
  ensureConformant?: boolean;
  /**
   * OBJECTS-section records an ENTITY of this document HARD-POINTS at, carried
   * in from the source drawing so the pointer still resolves in the written
   * file. The one case today is an `ACAD_TABLE`'s group-342 `TABLESTYLE`: a
   * WBLOCK builds a FROM-SCRATCH document holding the table (and its `*T<N>`
   * graphics block), and without its style record the 342 survives as a handle
   * that resolves to nothing — AutoCAD's wblock clone instead "clones hard
   * owners and hard pointers" (ObjectARX Developer's Guide, *Deep Clone
   * Basics*; groups 340–349 are the DXF hard-pointer range).
   *
   * They are an EXPORT INPUT rather than a `DrawingDocument` field on purpose:
   * `rawObjectGroups` — the only document-level carrier for an OBJECTS record —
   * is one of the by-construction IMPORTED-SOURCE signals
   * `isImportedSourceDocument` reads, so a from-scratch document that set it
   * would skip the conformant skeleton entirely. `ensureConformantSkeleton`
   * merges them into the skeleton's own OBJECTS stream (fresh minted handle,
   * dictionary owner + child entry, dangling pointers pruned) and rewrites the
   * referring entity's handle to match. Ignored unless `ensureConformant` is
   * set, and ignored for an IMPORTED document (it already carries its own
   * OBJECTS stream verbatim).
   */
  carriedObjectRecords?: readonly CarriedObjectRecord[];
  /**
   * T9 PR-3 — force `\U+XXXX` escaping of non-ASCII string content on/off.
   * Forwarded verbatim to {@link serializeDxfDocument}. When omitted the writer
   * derives it from the document's `$ACADVER` (pre-R2007 → escape; R2007+ →
   * raw UTF-8). SL-17: from-scratch conformant saves now adopt the R2018/AC1032
   * skeleton (`$ACADVER = AC1032` ≥ R2007), so the DERIVED default is raw UTF-8
   * (native R2018 text encoding) — this is the version-driven selector seam.
   * An imported doc keeps its own carried-forward `$ACADVER`, so a legacy
   * R2000/AC1015 source still escapes. Set this explicitly to override.
   */
  escapeUnicode?: boolean;
};

/* ── Style Table Conversion: DrawingModel → DXF ──────────── */

// T9b review round 1 — `drawingTextStyleToDxfEntry` moved to
// `dxfBridge.textStyleRecord.ts` (shared with the MLEADER stampers, which
// export.ts imports — a dependency-free home avoids an import cycle);
// re-exported here so every existing import path keeps resolving.
import { drawingTextStyleToDxfEntry } from "./dxfBridge.textStyleRecord";
export { drawingTextStyleToDxfEntry };

export function drawingDimStyleToDxfEntry(style: DimensionStyle): DxfDimensionStyleEntry {
  const dimVars: Record<string, string | number> = {};
  // Real-number variables — only emit when they differ from the default,
  // matching AutoCAD's convention of storing overrides only.
  if (style.DIMSCALE !== 1) dimVars.DIMSCALE = style.DIMSCALE;
  if (style.DIMASZ !== 2.5) dimVars.DIMASZ = style.DIMASZ;
  if (style.DIMEXO !== 0.625) dimVars.DIMEXO = style.DIMEXO;
  if (style.DIMDLI !== 3.75) dimVars.DIMDLI = style.DIMDLI;
  if (style.DIMEXE !== 1.25) dimVars.DIMEXE = style.DIMEXE;
  if (style.DIMRND !== 0) dimVars.DIMRND = style.DIMRND;
  if (style.DIMDLE !== 0) dimVars.DIMDLE = style.DIMDLE;
  if (style.DIMTP !== 0) dimVars.DIMTP = style.DIMTP;
  if (style.DIMTM !== 0) dimVars.DIMTM = style.DIMTM;
  if (style.DIMTXT !== 2.5) dimVars.DIMTXT = style.DIMTXT;
  if (style.DIMLFAC !== 1) dimVars.DIMLFAC = style.DIMLFAC;
  if (style.DIMTFAC !== 1) dimVars.DIMTFAC = style.DIMTFAC;
  if (style.DIMGAP !== 0.625) dimVars.DIMGAP = style.DIMGAP;
  if (style.DIMCLRD !== 0) dimVars.DIMCLRD = style.DIMCLRD;
  if (style.DIMCLRE !== 0) dimVars.DIMCLRE = style.DIMCLRE;
  if (style.DIMCLRT !== 0) dimVars.DIMCLRT = style.DIMCLRT;
  if (style.DIMDEC !== 4) dimVars.DIMDEC = style.DIMDEC;
  if (style.DIMLUNIT !== 2) dimVars.DIMLUNIT = style.DIMLUNIT;
  if (style.DIMPOST) dimVars.DIMPOST = style.DIMPOST;
  if (style.DIMAPOST) dimVars.DIMAPOST = style.DIMAPOST;
  // Arrow block names and text style reference
  if (style.DIMBLK) dimVars.DIMBLK = style.DIMBLK;
  if (style.DIMBLK1) dimVars.DIMBLK1 = style.DIMBLK1;
  if (style.DIMBLK2) dimVars.DIMBLK2 = style.DIMBLK2;
  if (style.DIMLDRBLK) dimVars.DIMLDRBLK = style.DIMLDRBLK;
  if (style.DIMTXSTY && style.DIMTXSTY !== "Standard") {
    dimVars.DIMTXSTY = style.DIMTXSTY;
  }
  // DIMSAH flag — explicit override OR derived from usage of DIMBLK1/DIMBLK2.
  // AutoCAD uses this to decide whether to honour DIMBLK or DIMBLK1/DIMBLK2.
  const usingSeparateArrows =
    style.DIMSAH || !!(style.DIMBLK1 || style.DIMBLK2);
  if (usingSeparateArrows) dimVars.DIMSAH = 1;
  // Boolean DIMVARs — only emit overrides so the default-shape round-trip
  // is unchanged.
  if (style.DIMTIH !== true) dimVars.DIMTIH = style.DIMTIH ? 1 : 0;
  if (style.DIMTOH !== true) dimVars.DIMTOH = style.DIMTOH ? 1 : 0;
  if (style.DIMSE1) dimVars.DIMSE1 = 1;
  if (style.DIMSE2) dimVars.DIMSE2 = 1;
  if (style.DIMSD1) dimVars.DIMSD1 = 1;
  if (style.DIMSD2) dimVars.DIMSD2 = 1;
  if (style.DIMTOL) dimVars.DIMTOL = 1;
  if (style.DIMLIM) dimVars.DIMLIM = 1;
  if (style.DIMTOFL) dimVars.DIMTOFL = 1;
  if (style.DIMSOXD) dimVars.DIMSOXD = 1;
  if (style.DIMTIX) dimVars.DIMTIX = 1;
  if (style.DIMUPT) dimVars.DIMUPT = 1;
  if (style.DIMALT) dimVars.DIMALT = 1;
  // Integer DIMVARs with non-default overrides
  if (style.DIMTAD !== 0) dimVars.DIMTAD = style.DIMTAD;
  if (style.DIMJUST !== 0) dimVars.DIMJUST = style.DIMJUST;
  if (style.DIMATFIT !== 3) dimVars.DIMATFIT = style.DIMATFIT;
  if (style.DIMTOLJ !== 1) dimVars.DIMTOLJ = style.DIMTOLJ;
  if (style.DIMTDEC !== 4) dimVars.DIMTDEC = style.DIMTDEC;
  if (style.DIMTMOVE !== 0) dimVars.DIMTMOVE = style.DIMTMOVE;
  // Real-number text DIMVARs
  if (style.DIMTVP !== 0) dimVars.DIMTVP = style.DIMTVP;
  if (style.DIMTSZ !== 0) dimVars.DIMTSZ = style.DIMTSZ;
  // Line weights
  if (style.DIMLWD !== -2) dimVars.DIMLWD = style.DIMLWD;
  if (style.DIMLWE !== -2) dimVars.DIMLWE = style.DIMLWE;
  // Center marks size / line style
  if (style.DIMCEN !== 2.5) dimVars.DIMCEN = style.DIMCEN;
  // Zero suppression bitmasks
  if (style.DIMZIN !== 0) dimVars.DIMZIN = style.DIMZIN;
  if (style.DIMTZIN !== 0) dimVars.DIMTZIN = style.DIMTZIN;
  if (style.DIMAZIN !== 0) dimVars.DIMAZIN = style.DIMAZIN;
  // Decimal separator + fraction format
  if (style.DIMDSEP !== 46) dimVars.DIMDSEP = style.DIMDSEP;
  if (style.DIMFRAC !== 0) dimVars.DIMFRAC = style.DIMFRAC;
  // Angular units
  if (style.DIMAUNIT !== 0) dimVars.DIMAUNIT = style.DIMAUNIT;
  if (style.DIMADEC !== 0) dimVars.DIMADEC = style.DIMADEC;
  // Alternate units
  if (style.DIMALTD !== 2) dimVars.DIMALTD = style.DIMALTD;
  if (style.DIMALTF !== 25.4) dimVars.DIMALTF = style.DIMALTF;
  if (style.DIMALTU !== 2) dimVars.DIMALTU = style.DIMALTU;
  if (style.DIMALTRND !== 0) dimVars.DIMALTRND = style.DIMALTRND;
  if (style.DIMALTTD !== 2) dimVars.DIMALTTD = style.DIMALTTD;
  if (style.DIMALTZ !== 0) dimVars.DIMALTZ = style.DIMALTZ;
  if (style.DIMALTTZ !== 0) dimVars.DIMALTTZ = style.DIMALTTZ;
  // Annotative flag (Group C) — carried as a dedicated dimVar so the
  // DXF writer can fold it back into the DIMSTYLE flag byte (group 70
  // bit 8) while preserving a clean round-trip through the parsed
  // dimVars map.
  if (style.DIMANNO !== 0) dimVars.DIMANNO = style.DIMANNO;
  // Include extra round-trip dimVars last so they don't clobber known fields
  if (style.extraDimVars) {
    for (const [key, value] of Object.entries(style.extraDimVars)) {
      if (!(key in dimVars)) dimVars[key] = value;
    }
  }
  const entry: DxfDimensionStyleEntry = { name: style.name, dimVars };
  // SL-4 — re-emit the DIMSTYLE record's own group-105 handle carried through
  // the model (the writer serializes it at code 105, not 5). A handle-less
  // style (from-scratch Standard) emits no record handle, unchanged.
  if (style.handle !== undefined) entry.handle = style.handle;
  // T9b — the record's `330` owner (DIMSTYLE table head).
  if (style.ownerHandle !== undefined) entry.ownerHandle = style.ownerHandle;
  // ROOT E — thread the subclass-marker presence so the writer suppresses the
  // 100/ groups for a parsed R12 source (`false`) and emits them for a fresh /
  // skeleton style (`undefined`).
  if (style.hadSubclassMarker !== undefined) {
    entry.hadSubclassMarker = style.hadSubclassMarker;
  }
  return entry;
}

export function exportLayers(layers: LayerDefinition[]): DxfLayerEntry[] {
  return layers.map((layer): DxfLayerEntry => {
    const entry: DxfLayerEntry = {
      name: layer.name,
      // Negative color = layer off
      color: layer.visible ? layer.color : -layer.color,
      // Reconstruct flags: bit 0 = frozen, bit 2 = locked
      flags: (layer.frozen ? 1 : 0) | (layer.locked ? 4 : 0),
      linetype: layer.linetype,
      lineweight: layer.lineweight,
      plottable: layer.plottable,
    };
    if (layer.handle) entry.handle = layer.handle;
    // T9b — `330` owner (LAYER table head) + `390` plot-style + `347` material
    // pointers: imported verbatim, or resolved by the conformant skeleton.
    if (layer.ownerHandle) entry.ownerHandle = layer.ownerHandle;
    if (layer.plotStyleHandle) entry.plotStyleHandle = layer.plotStyleHandle;
    if (layer.materialHandle) entry.materialHandle = layer.materialHandle;
    if (layer.description) entry.description = layer.description;
    return entry;
  });
}

/**
 * FIX-4 (Codex#2 PR-LT2 P2) — collect the set of LOWERCASED linetype names
 * actually REFERENCED by a drawing: every `entity.linetype` (across all spaces'
 * entities AND block-definition children), every `layer.linetype`, and every
 * `mleaderStyle.leaderLineType`. Used by the LTYPE export filter so a
 * synthetic-seed linetype that is USED survives the round-trip even though
 * unused seed entries are dropped. Mirrors exactly the fields the linetype
 * refcheck resolves (referenceAudit.{entityRefs,structural}.ts).
 *
 * FIX-2 (Codex PR-LT2) — ALSO scan UNSUPPORTED entities preserved verbatim in
 * `document.rawEntityGroups` and every `block.rawEntityGroups`: such a raw
 * entity may carry a linetype via DXF group code `6` (the entity-linetype-name
 * group). That reference is invisible to the typed `entity.linetype` scan above,
 * so without it the synthetic-seed filter could DROP a linetype that a raw
 * passthrough entity still references — leaving the round-tripped entity
 * pointing at a now-missing LTYPE record. Group 6 holds the linetype NAME
 * directly (not a handle); ByLayer/ByBlock/Continuous sentinels are harmless to
 * include since they are always emitted regardless.
 *
 * FIX-1 (Codex PR-LT2 P2 — snapshot-source parity) — when the caller routes the
 * export through a per-space `entitiesSnapshot` (the compact-cache read-through
 * wrapper), the ENTITIES serializer iterates `entitiesSnapshot[space.id] ??
 * space.entities`. This collector MUST scan the SAME effective per-space source:
 * otherwise a snapshot entity using a standard seed linetype (e.g. `DASHED`)
 * while `document.spaces[].entities` is stale/empty would leave the seed UNUSED
 * in this set → `exportLinetypes` DROPS the LTYPE record → the emitted snapshot
 * entity's group-6 reference DANGLES. The optional `entitiesSnapshot` argument
 * threads the orchestrator's map down so the two views never diverge; absent /
 * undefined falls through to `space.entities` (legacy direct iteration).
 *
 * Pure + allocation-light: a single lowercased name set. Empty / missing fields
 * are simply skipped.
 */
export function collectReferencedLinetypeNames(
  document: DrawingDocument,
  entitiesSnapshot?: Map<string, DrawingEntity[]>,
): Set<string> {
  const names = new Set<string>();
  const add = (name: string | undefined): void => {
    if (name) names.add(name.toLowerCase());
  };
  // Scan a raw-entity-group list for group-6 (entity linetype NAME) references.
  const addRawGroups = (
    rawGroups: ReadonlyArray<RawEntityGroup> | undefined,
  ): void => {
    for (const raw of rawGroups ?? []) {
      for (const g of raw.groups) {
        if (g.code === 6) add(g.value.trim());
      }
    }
  };
  // entity.linetype across every space's entities. FIX-1 — prefer the same
  // per-space snapshot the serializer uses so a snapshot-only linetype ref is
  // counted (parity with `exportDrawingDocumentToDxfDocument`'s entity source).
  for (const space of document.spaces) {
    const sourceEntities = entitiesSnapshot?.get(space.id) ?? space.entities;
    for (const entity of sourceEntities) add(entity.linetype);
  }
  // entity.linetype across block-definition children + raw block children.
  for (const block of Object.values(document.blocks ?? {})) {
    for (const child of block.entities) {
      add((child as { linetype?: string }).linetype);
    }
    addRawGroups(block.rawEntityGroups);
  }
  // Unsupported ENTITIES-section siblings preserved verbatim (group 6).
  addRawGroups(document.rawEntityGroups);
  // layer.linetype + mleaderStyle.leaderLineType.
  for (const layer of document.styleTables?.layers ?? []) add(layer.linetype);
  for (const ms of document.styleTables?.multileaderStyles ?? []) {
    add(ms.leaderLineType);
  }
  return names;
}

/**
 * FIX-2 (Codex PR-LT2 P2) — collect the LOWERCASED linetype names referenced by
 * the parts of a drawing visible to `exportStyleTables`: every `layer.linetype`
 * and every `mleaderStyle.leaderLineType` in the supplied `StyleTables`. Used as
 * the ROBUST default for `exportStyleTables`'s `referencedLinetypeNames` arg so a
 * direct caller (not routing through `collectReferencedLinetypeNames(document)`)
 * never drops a linetype its layers / mleader styles still point at. A SUBSET of
 * `collectReferencedLinetypeNames` (which also scans entity + raw-group refs from
 * the full document — those are out of this function's view).
 */
export function collectStyleTableReferencedLinetypeNames(
  styleTables: StyleTables | undefined,
): Set<string> {
  const names = new Set<string>();
  if (!styleTables) return names;
  for (const layer of styleTables.layers ?? []) {
    if (layer.linetype) names.add(layer.linetype.toLowerCase());
  }
  for (const ms of styleTables.multileaderStyles ?? []) {
    if (ms.leaderLineType) names.add(ms.leaderLineType.toLowerCase());
  }
  return names;
}

/**
 * FIX-2 (Codex PR-LT2 P2) — remove any raw `0/TABLE 2/LTYPE … 0/ENDTAB` block
 * from a FLATTENED `rawTableGroups` list. The TABLES section's `rawTableGroups`
 * is a single flat `DxfGroup[]` concatenating every passthrough table; each
 * table opens with `0/TABLE` followed by a `2/<name>` table-name marker and
 * closes with `0/ENDTAB`. When the typed LTYPE export path is active the raw
 * LTYPE block would be emitted IN ADDITION to the typed one (duplicate table),
 * so we drop exactly the LTYPE block(s) here while preserving every other table
 * (VPORT / VIEW / UCS / APPID / …) verbatim, including stray groups outside any
 * `0/TABLE` boundary. Pure — returns a new array, never mutates the input.
 */
export function stripRawLinetypeTable(
  rawTableGroups: ReadonlyArray<DxfGroup>,
): DxfGroup[] {
  const out: DxfGroup[] = [];
  let i = 0;
  const n = rawTableGroups.length;
  while (i < n) {
    const g = rawTableGroups[i]!;
    if (g.code === 0 && g.value === "TABLE") {
      // Look ahead to the table-name marker (the first `2/<name>` after the
      // `0/TABLE` opener) to decide whether this is the LTYPE table. The name
      // marker is conventionally the next group, but tolerate intervening
      // table-level groups by scanning forward until ENDTAB or the next TABLE.
      let j = i + 1;
      let tableName: string | undefined;
      while (j < n) {
        const gj = rawTableGroups[j]!;
        if (gj.code === 0) break; // 0/ENDTAB (or a malformed next 0/TABLE)
        if (gj.code === 2 && tableName === undefined) {
          tableName = gj.value.trim();
          break;
        }
        j++;
      }
      if (tableName === "LTYPE") {
        // Skip the whole block: from this `0/TABLE` up to and including the
        // matching `0/ENDTAB` (or the end of the list if the block is unclosed).
        let k = i + 1;
        while (k < n) {
          const gk = rawTableGroups[k]!;
          if (gk.code === 0 && gk.value === "ENDTAB") {
            k++; // consume ENDTAB
            break;
          }
          if (gk.code === 0 && gk.value === "TABLE") break; // unclosed — stop
          k++;
        }
        i = k;
        continue;
      }
    }
    out.push(g);
    i++;
  }
  return out;
}

export function exportStyleTables(
  styleTables: StyleTables | undefined,
  existingTablesSection: DxfTablesSection | null,
  blockRecords: DxfBlockRecordEntry[] = [],
  linetypes: Record<string, Linetype> | undefined = undefined,
  referencedLinetypeNames: ReadonlySet<string> | undefined = undefined,
  documentLinetypeTableHeader: DxfGroup[] | undefined = undefined,
  // FIX-110 — the LAYER / STYLE / DIMSTYLE / BLOCK_RECORD table heads persisted
  // on the document (`document.symbolTableHeaders`), keyed by table name. The
  // orchestrator threads `document.symbolTableHeaders`; the four typed-table
  // writers re-emit the real `5/<handle>` head from this map instead of emitting
  // a head-less table that ezdxf silently repairs (→ AutoCAD recovery). Optional
  // — a direct caller / in-memory doc without heads gets the prior head-less
  // behaviour, preserving backward compatibility for non-conformant callers.
  documentSymbolTableHeaders:
    Record<string, DxfGroup[]> | undefined = undefined,
): DxfTablesSection {
  const textStyles = styleTables?.textStyles?.map(drawingTextStyleToDxfEntry) ?? [];
  const dimensionStyles = styleTables?.dimensionStyles?.map(drawingDimStyleToDxfEntry) ?? [];
  const layers = styleTables?.layers ? exportLayers(styleTables.layers) : [];
  const forwardedRawTableGroups = existingTablesSection?.rawTableGroups ?? [];
  // FIX-2 (Codex PR-LT2 P2) — `exportStyleTables` is PUBLIC API. The seed-filter
  // (FIX-4) drops synthetic-seed linetypes NOT in `referencedLinetypeNames`. The
  // orchestrator (`exportDrawingDocumentToDxfDocument`) always precomputes that
  // set via `collectReferencedLinetypeNames(document)` and passes it. But a
  // caller invoking this PUBLIC API WITHOUT the arg previously got an EMPTY
  // default → the filter would drop a seed linetype that LAYERS / MLEADER styles
  // actually reference, leaving exported layers pointing at a now-missing LTYPE.
  // Make the default ROBUST: when no set is supplied, compute one from the data
  // this function CAN see (the `styleTables` layer / mleader linetype refs) so
  // the default path NEVER drops a referenced linetype. (Entity / raw-group refs
  // live on the full document, which this API does not receive — the
  // orchestrator's explicit set covers those; this default covers the
  // style-table refs a direct caller would otherwise lose.)
  const effectiveReferencedNames =
    referencedLinetypeNames ??
    collectStyleTableReferencedLinetypeNames(styleTables);
  // PR-LT2 — emit the LTYPE table from the typed `document.linetypes`. Because
  // the parser promoted LTYPE OUT of `rawTableGroups`, this typed array is the
  // ONLY source of LTYPE groups in the output — serialized exactly once, no
  // duplication (plan §10.3 / Codex#1 F10). Re-emits raw 40 / 73 via
  // patternLengthRaw / elementCountRaw for byte-exact plain round-trip.
  // FIX-4 — pass the referenced-name set so UNUSED synthetic-seed linetypes are
  // dropped (AutoCAD's LTYPE table = defined + used only) while reserved + used
  // + file-originated entries survive.
  //
  // FIX-1 (Codex PR-LT2 P2 — direct-caller API regression) — the `linetypes`
  // argument is OPTIONAL. The orchestrator (`exportDrawingDocumentToDxfDocument`)
  // always passes the typed `document.linetypes`, but a DIRECT parse→export
  // caller that forwards the parsed `existingTablesSection` and OMITS the new arg
  // would emit NOTHING for LTYPE: the parser PROMOTED the LTYPE table out of
  // `rawTableGroups` into the typed `existingTablesSection.linetypes`
  // (`DxfLinetypeEntry[]`), so `rawTableGroups` no longer carries it and the
  // undefined arg yields `[]` → every parsed LTYPE record (and its header) is
  // dropped. When the typed arg is OMITTED, fall back to the parsed records the
  // section already carries — they are ALREADY in the dxf-io `DxfLinetypeEntry`
  // shape `exportLinetypes` produces, so a parse→export round-trip via the public
  // API preserves the LTYPE table. (Passing an EXPLICIT `{}` / empty record still
  // means "no linetypes" — only an undefined arg triggers the fallback.)
  const linetypeEntries =
    linetypes !== undefined
      ? exportLinetypes(linetypes, effectiveReferencedNames)
      : (existingTablesSection?.linetypes ?? []);
  // Carry the captured LTYPE table-LEVEL header (table handle / owner /
  // subclass / max-entries) so the writer re-emits the real header verbatim
  // (Codex#2 P2 — table header round-trip). FIX-3 — PREFER the document-level
  // header (`document.linetypeTableHeader`, persisted at import) over the
  // transient parsed `existingTablesSection`: on a pure MODEL round-trip the
  // parsed section is gone, so without the document field the header would be
  // lost. Falls back to the parsed section for callers that still pass one.
  const linetypeTableHeader =
    documentLinetypeTableHeader ?? existingTablesSection?.linetypeTableHeader;

  // FIX-2 (Codex PR-LT2 P2 — no DUPLICATE LTYPE table) — the typed LTYPE export
  // path assumes the raw TABLES passthrough no longer carries an LTYPE table (the
  // live parser promotes LTYPE OUT of `rawTableGroups` into the typed array). But
  // a PERSISTED / pre-PR document, or a PUBLIC caller, may forward an
  // `existingTablesSection.rawTableGroups` that STILL contains a raw
  // `0/TABLE 2/LTYPE … 0/ENDTAB` block AND now also gets typed `linetypes`. The
  // writer emits the raw passthrough first, then the typed table → TWO LTYPE
  // tables with conflicting definitions / counts. When the typed branch is active
  // (`linetypeEntries.length > 0`), STRIP the raw LTYPE block so ONLY the typed
  // table is emitted. Mirrors how STYLE / LAYER / DIMSTYLE are already routed out
  // of `rawTableGroups` into typed arrays by the parser (sectionParsers.ts) — the
  // de-dupe precedent — applied here at export for the persisted / public-caller
  // path the parser never touched.
  const rawTableGroups =
    linetypeEntries.length > 0
      ? stripRawLinetypeTable(forwardedRawTableGroups)
      : forwardedRawTableGroups;

  // FIX-110 — re-emit the LAYER / STYLE / DIMSTYLE / BLOCK_RECORD table heads.
  // PREFER the document-level map (persisted at import / adopted by the
  // conformant skeleton) over the transient parsed `existingTablesSection` —
  // identical reasoning to the LTYPE `documentLinetypeTableHeader ?? section`
  // precedence above (a pure MODEL round-trip has no parsed section). The four
  // typed-table writers re-emit the real `5/<handle>` head from this map; absent
  // a head they fall back to the prior head-less behaviour.
  const symbolTableHeaders =
    documentSymbolTableHeaders ?? existingTablesSection?.symbolTableHeaders;

  return {
    kind: "tables-section",
    textStyles,
    dimensionStyles,
    layers,
    blockRecords,
    ...(linetypeEntries.length > 0 ? { linetypes: linetypeEntries } : {}),
    // FIX (Codex PR-LT2 P2 — header-only LTYPE table round-trip): forward the
    // captured `linetypeTableHeader` whenever a header EXISTS, REGARDLESS of
    // whether any records survive the seed-filter. The iter-9 writer (FIX-A)
    // emits an explicitly-empty LTYPE table (header groups + `70/0`, zero
    // records) when it sees a header but no records — but only if the header
    // reaches it. The old `linetypeEntries.length > 0` guard DROPPED a captured
    // header on the records-empty path (e.g. a parsed `existingTablesSection`
    // whose LTYPE table had a header + `70/0` but ZERO records, reachable via the
    // public `exportStyleTables` parse→export path), losing the header-only table
    // the writer is now able to preserve. ABSENCE semantics are intact: a doc
    // with NO captured header (`linetypeTableHeader` undefined) still adds NO
    // header here, so a doc that never had an LTYPE table never gains one.
    ...(linetypeTableHeader ? { linetypeTableHeader } : {}),
    ...(symbolTableHeaders && Object.keys(symbolTableHeaders).length > 0
      ? { symbolTableHeaders }
      : {}),
    rawTableGroups,
  };
}

/**
 * Build the {@link HeaderLinetypeVars} payload from a document's typed
 * linetype-scale fields (`ltscale/celtscale/psltscale/msltscale`). Only finite
 * numeric values are included so an in-memory `DrawingDocument` partial that
 * never set them (the fields are schema-defaulted, so a validated doc always
 * has all four) contributes nothing rather than emitting `NaN`. The result is
 * patched into the HEADER section by `exportDrawingDocumentToDxfDocument`.
 */
export function collectLinetypeVars(
  document: DrawingDocument,
): HeaderLinetypeVars {
  const vars: HeaderLinetypeVars = {};
  if (Number.isFinite(document.ltscale)) vars.ltscale = document.ltscale;
  if (Number.isFinite(document.celtscale)) vars.celtscale = document.celtscale;
  if (Number.isFinite(document.psltscale)) vars.psltscale = document.psltscale;
  if (Number.isFinite(document.msltscale)) vars.msltscale = document.msltscale;
  return vars;
}

/**
 * Build the {@link HeaderDocumentVars} payload from a document's typed
 * document-level fields (`insunits` / `pdmode` / `pdsize`) — Batch-6
 * S-HEADER-INSUNITS + S-HEADER-PDMODE-PDSIZE.
 *
 * ALL finite values are passed through, INCLUDING 0 (Codex #248 F1 round-2).
 * The "invent absent default vs overwrite existing var" decision no longer
 * lives here — it lives in the patch step (`patchNumericVarsIntoHeaderGroups`
 * via each spec's `defaultValue: 0`):
 *
 *   - A var ALREADY PRESENT in the source HEADER is overwritten in-place with
 *     the current value, INCLUDING 0 — so a drawing imported with a non-zero
 *     `$INSUNITS`/`$PDMODE`/`$PDSIZE` then edited back to 0 exports the edited
 *     0, not the stale raw value. (Round-1 skipped 0 here → `hasDocumentVars`
 *     went false → the patch never ran → the stale value survived. That was the
 *     bug.)
 *   - A var ABSENT from the source HEADER is only appended when non-zero, so a
 *     default-0 source is never invented a `$INSUNITS`/`$PDMODE`/`$PDSIZE`.
 *
 * Passing 0 through here also keeps `hasDocumentVars` true whenever the typed
 * fields are materialised, ensuring the patch always runs to reflect edits.
 */
export function collectDocumentVars(
  document: DrawingDocument,
): HeaderDocumentVars {
  const vars: HeaderDocumentVars = {};
  if (Number.isFinite(document.insunits)) vars.insunits = document.insunits;
  if (Number.isFinite(document.pdmode)) vars.pdmode = document.pdmode;
  if (Number.isFinite(document.pdsize)) vars.pdsize = document.pdsize;
  if (Number.isFinite(document.mirrtext)) vars.mirrtext = document.mirrtext;
  // $FILLMODE — passed through unconditionally (including the default 1) for
  // the same reason as the 0-default siblings: the "invent absent default vs
  // overwrite an existing var" decision lives in the patch step (spec
  // `defaultValue: 1`), and keeping the field here keeps `hasDocumentVars`
  // true so an edit back to the default still reaches a present `$FILLMODE`.
  if (Number.isFinite(document.fillmode)) vars.fillmode = document.fillmode;
  // $CECOLOR — project the typed EntityColor union back to the HEADER scalar.
  // Always pass through (the spec's `defaultValue: 256` omits a standalone
  // ByLayer + overwrites a present var in-place per the existing contract).
  // `document.cecolor` is always materialised by the import (default ByLayer).
  if (document.cecolor !== undefined) {
    vars.cecolor = cecolorToHeader(document.cecolor);
  }
  return vars;
}

/**
 * Build the {@link HeaderSnapVars} payload from a document's typed running
 * object-snap field (`osmode`) — OSNAP-HEADER-ROUNDTRIP.
 *
 * The finite value is ALWAYS passed through, INCLUDING the default (4133).
 * The "invent absent default vs overwrite existing var" decision lives in the
 * patch step (`patchSnapVarsIntoHeaderGroups` via the spec's
 * `defaultValue: 4133`), mirroring `collectDocumentVars`:
 *
 *   - A `$OSMODE` ALREADY PRESENT in the source HEADER is overwritten in-place
 *     with the current value (including the default 4133) — so a drawing
 *     imported with a non-default `$OSMODE` then edited back to 4133 exports
 *     the edited value, not the stale raw bytes.
 *   - A `$OSMODE` ABSENT from the source HEADER is only appended when it
 *     differs from 4133, so a default-valued source never invents a `$OSMODE`.
 *
 * Passing the value through here unconditionally keeps `hasSnapVars` true
 * whenever the typed field is materialised, ensuring the patch always runs to
 * reflect edits.
 */
export function collectSnapVars(document: DrawingDocument): HeaderSnapVars {
  const vars: HeaderSnapVars = {};
  if (Number.isFinite(document.osmode)) vars.osmode = document.osmode;
  return vars;
}

/**
 * Build the {@link HeaderPointVars} payload from a document's typed point
 * fields ($INSBASE ← `doc.insertionBase`). Always passed through when the
 * coordinates are finite — INCLUDING the default (0,0,0). The "invent absent
 * default vs overwrite existing var" decision lives in the patch step
 * (`patchPointVarsIntoHeaderGroups` via the spec's `defaultValue`): a var
 * already present in the source HEADER is overwritten in-place (so an edit
 * back to (0,0,0) persists), while an absent-in-source default is never
 * appended into the output.
 */
export function collectPointVars(
  document: DrawingDocument,
): HeaderPointVars {
  const vars: HeaderPointVars = {};
  const base = document.insertionBase;
  if (
    base &&
    Number.isFinite(base.x) &&
    Number.isFinite(base.y) &&
    Number.isFinite(base.z)
  ) {
    vars.insertionBase = { x: base.x, y: base.y, z: base.z };
  }
  return vars;
}

/**
 * Re-resolve MLEADER block-content handles (AcDbMLeader BODY group 344 =
 * block_record_handle) after the content block reference is edited.
 *
 * The bridge carries the source 344 handle verbatim on
 * `geometry.blockRecordHandle`, and the writer PREFERS it over `blockName` so a
 * round-trip is byte-faithful (Codex #271). But when the user changes the
 * MLEADER content block — edits `blockName` to a DIFFERENT block — the stored
 * handle still points at the OLD BLOCK_RECORD, so export would emit the stale
 * handle and re-import would resolve back to the old block (or an unresolved
 * handle); the edit is lost (Codex #285-8).
 *
 * Using the document's BLOCK_RECORD name↔handle population (the same entries the
 * writer emits) we detect divergence: when the stored handle resolves to a block
 * whose name no longer matches `blockName`, RECOMPUTE the handle for the edited
 * block so the writer emits the new BLOCK_RECORD's handle (re-import resolves to
 * the edited block). When the edited name has no BLOCK_RECORD entry, DROP the
 * stale handle so the writer falls back to emitting `blockName` (group 344 by
 * name). An UNCHANGED reference (resolved name === blockName) is left VERBATIM.
 *
 * Edit-vs-passthrough disambiguation leans on the import invariant: the pass-2
 * resolver (`resolveMLeaderBlockHandles`) stamps `blockName` from the handle
 * IFF the handle resolves, and leaves it UNSET otherwise. Hence (#294 Codex
 * P2 follow-ups to #285-8):
 *   • ORPHAN handle + non-empty name — the name cannot have come from import
 *     (an unresolvable 344 imports with blockName unset), so it is an EDIT:
 *     recompute the handle when the edited name resolves to a live
 *     BLOCK_RECORD; keep the source handle verbatim ONLY when the name ALSO
 *     does not resolve (true passthrough — never fabricate).
 *   • RESOLVABLE handle + EMPTY name — import always names a resolvable
 *     handle, so an empty name means the user CLEARED the content block:
 *     drop the stale handle (the writer then emits no 344 instead of silently
 *     restoring the old block).
 *   • ORPHAN handle + EMPTY name — the genuine external/orphan passthrough
 *     state the import produces → verbatim.
 *
 * Every recompute/drop of the BODY 344 is MIRRORED onto the copied
 * CONTEXT_DATA BlockData handle (group 341 — `contextData.blockData
 * .blockRecordHandle`, primary + any secondary contexts): the faithful writer
 * emits that 341 BEFORE the body 344, so leaving it stale would export
 * CONFLICTING block references and context-honoring consumers would still
 * render the OLD block (#294 Codex P2, context-BlockData arm). The mirror only
 * touches a context copy that pointed at the SAME record the body did — an
 * independently-divergent context 341 (weird source) is preserved verbatim.
 *
 * CONTEXT-ONLY references (#294 r2): when the block reference lives ONLY in
 * CONTEXT_DATA BlockData 341 (no body 344) the body-keyed pass above never
 * fires, so a `blockName` edit would re-emit the stale context 341. The
 * `!handle` arm applies the same decision matrix directly to each context
 * copy via `restampContextOnlyMLeaderBlockHandles` (see its doc-comment for
 * the per-copy matrix and the inverted empty-name semantics). #294 r3: when
 * the edited name resolves, that helper ALSO stamps the BODY handle so the
 * writer emits a real 344 instead of the name-as-344 fallback the import
 * resolver cannot decode (the edit was lost on re-import); the unedited
 * (empty-name) entity stays body-less verbatim.
 *
 * Handle keys are hex + case-insensitive (same convention as the parser's
 * resolveMLeaderBlockHandles); block names are DXF case-insensitive.
 */
function stampMLeaderBlockContentHandles(
  entities: DxfEntity[],
  blockRecords: DxfBlockRecordEntry[],
): void {
  const handleToName = new Map<string, string>();
  const nameToHandle = new Map<string, string>();
  for (const rec of blockRecords) {
    if (!rec.handle || !rec.name) continue;
    handleToName.set(rec.handle.toLowerCase(), rec.name);
    // First write wins so the name → handle direction is deterministic when two
    // records somehow share a name (keeps the earliest BLOCK_RECORD's handle).
    if (!nameToHandle.has(rec.name.toLowerCase())) {
      nameToHandle.set(rec.name.toLowerCase(), rec.handle);
    }
  }
  if (handleToName.size === 0) return;
  for (const entity of entities) {
    if (entity.kind !== "supported-entity" || entity.type !== "MLEADER") {
      continue;
    }
    const g = entity.geometry;
    const handle = g.blockRecordHandle;
    const name = g.blockName;
    if (!handle) {
      // CONTEXT-ONLY arm (#294 r2): the block reference may live ONLY in a
      // CONTEXT_DATA BlockData 341 (primary or secondary) with NO body 344.
      // Import never stamps `blockName` from a context 341 (the resolver is
      // body-344-keyed), so an EMPTY name is the unedited import state →
      // verbatim passthrough; a NON-EMPTY name is an EDIT / model-authored
      // reference → apply the same resolve/recompute/drop matrix directly to
      // each context copy (otherwise the export re-emits the stale 341 and
      // context-honoring consumers keep the OLD block) AND, when the name
      // resolves, stamp the real BODY 344 handle too (#294 r3 — the writer's
      // name-as-344 fallback is import-undecodable, so the edit was lost).
      if (name) {
        restampContextOnlyMLeaderBlockHandles(g, name, handleToName, nameToHandle);
      }
      continue;
    }
    const resolvedName = handleToName.get(handle.toLowerCase());
    if (!name) {
      // Cleared-reference arm: a RESOLVABLE handle with an EMPTY name means
      // the user cleared the content block (import always stamps blockName
      // for a resolvable 344) → drop the stale handle so the writer emits no
      // 344. An ORPHAN handle with no name is the true passthrough state →
      // verbatim.
      if (resolvedName !== undefined) {
        restampMLeaderBlockHandles(g, handle, undefined);
      }
      continue;
    }
    const newHandle = nameToHandle.get(name.toLowerCase());
    if (resolvedName === undefined) {
      // Orphan source handle + non-empty name = EDIT (see doc-comment).
      // Recompute when the edited name resolves; verbatim when it does not
      // (true passthrough — never fabricate a handle for an unknown name).
      if (newHandle !== undefined) {
        restampMLeaderBlockHandles(g, handle, newHandle);
      }
      continue;
    }
    // Reference unchanged → keep the byte-faithful source handle.
    if (resolvedName.toLowerCase() === name.toLowerCase()) continue;
    // Reference CHANGED. Recompute the handle for the edited block, or drop the
    // stale handle so the writer falls back to emitting the (edited) blockName.
    restampMLeaderBlockHandles(g, handle, newHandle);
  }
}

/**
 * Apply one body-344 recompute/drop decision to an MLEADER geometry:
 * `newHandle === undefined` drops the stale handle (writer falls back to
 * `blockName` / emits no 344 when the name is empty), otherwise the body
 * handle is re-pointed at the edited block's BLOCK_RECORD. The SAME decision
 * is mirrored onto every copied CONTEXT_DATA BlockData handle (group 341,
 * primary `contextData` + each `secondaryContexts[]` entry) that pointed at
 * the SAME old record — the faithful writer emits 341 from those copies, so a
 * stale copy would ship a conflicting block reference (#294 Codex P2). A
 * context BlockData whose 341 differs from the old body 344 is left verbatim
 * (preserve-source-never-infer — no body decision provably applies to it).
 */
function restampMLeaderBlockHandles(
  g: DxfMLeaderEntity["geometry"],
  oldHandle: string,
  newHandle: string | undefined,
): void {
  if (newHandle !== undefined) g.blockRecordHandle = newHandle;
  else delete g.blockRecordHandle;
  const oldKey = oldHandle.toLowerCase();
  const blockDatas = mleaderContextBlockDatas(g);
  for (const bd of blockDatas) {
    if (!bd?.blockRecordHandle) continue;
    if (bd.blockRecordHandle.toLowerCase() !== oldKey) continue;
    if (newHandle !== undefined) bd.blockRecordHandle = newHandle;
    else delete bd.blockRecordHandle;
  }
}

/** Every copied CONTEXT_DATA BlockData of an MLEADER geometry — the primary
 * `contextData` plus each `secondaryContexts[]` entry (#294 Codex P2). */
function mleaderContextBlockDatas(
  g: DxfMLeaderEntity["geometry"],
): Array<NonNullable<DxfMLeaderEntity["geometry"]["contextData"]>["blockData"]> {
  return [
    g.contextData?.blockData,
    ...(g.secondaryContexts ?? []).map((sec) => sec.context.blockData),
  ];
}

/**
 * CONTEXT-ONLY arm of the MLEADER block-handle restamp (#294 r2): the block
 * reference lives ONLY in CONTEXT_DATA BlockData 341 (no body 344), so the
 * body-keyed decision matrix above never runs. Import leaves `blockName`
 * UNSET for a body-less MLEADER (`resolveMLeaderBlockHandles` is 344-keyed),
 * hence a NON-EMPTY name here is provably an EDIT / model-authored reference
 * — apply the same resolve/recompute/drop matrix PER context copy (each 341
 * is resolved on its own merit; there is no single body handle to mirror):
 *   • ctx 341 resolves to the SAME name — reference unchanged → verbatim.
 *   • ctx 341 resolves to a DIFFERENT name — reference CHANGED → recompute
 *     the handle for the edited block, or DROP the stale 341 when the edited
 *     name has no BLOCK_RECORD (never ship a reference to the OLD block).
 *   • ORPHAN ctx 341 — recompute when the edited name resolves; verbatim when
 *     it does not (true passthrough — never fabricate a handle).
 * The EMPTY-name case never reaches this helper: unlike the body arm (where
 * import always names a resolvable 344, so empty = cleared), empty IS the
 * unedited import state for a context-only reference and must stay verbatim
 * (preserve-source-never-infer).
 *
 * #294 r3 — a RESOLVABLE edited name is ALSO persisted on the BODY handle
 * (`g.blockRecordHandle`, group 344). Rewriting only the context 341 copies
 * left the body-less entity to the writer's name-as-344 fallback
 * (`pushGroup(344, g.blockName)`), but the import resolver treats body 344
 * ONLY as a BLOCK_RECORD handle — the next import saw an orphan "handle"
 * equal to the block NAME and lost the edited `blockName` even though the
 * block exists. Stamping the resolved handle makes the writer emit a REAL
 * body 344, so the edit survives re-import (and AutoCAD sees the block
 * reference at the entity level too). An UNRESOLVABLE name stamps nothing
 * (never fabricate a handle), and the unedited (empty-name) entity never
 * reaches this helper — it stays body-less verbatim.
 */
function restampContextOnlyMLeaderBlockHandles(
  g: DxfMLeaderEntity["geometry"],
  name: string,
  handleToName: Map<string, string>,
  nameToHandle: Map<string, string>,
): void {
  const newHandle = nameToHandle.get(name.toLowerCase());
  // #294 r3 — persist the edit at the ENTITY level: a resolvable edited name
  // gains the real body 344 handle (see doc-comment above).
  if (newHandle !== undefined) g.blockRecordHandle = newHandle;
  const blockDatas = mleaderContextBlockDatas(g);
  for (const bd of blockDatas) {
    if (!bd?.blockRecordHandle) continue;
    const resolvedName = handleToName.get(bd.blockRecordHandle.toLowerCase());
    if (resolvedName === undefined) {
      // Orphan ctx 341 + non-empty name = EDIT: recompute when the edited
      // name resolves, keep the source handle verbatim when it does not.
      if (newHandle !== undefined) bd.blockRecordHandle = newHandle;
      continue;
    }
    // Reference unchanged → keep the byte-faithful source handle.
    if (resolvedName.toLowerCase() === name.toLowerCase()) continue;
    // Reference CHANGED → recompute, or drop the stale 341 so the writer
    // emits no context handle for the edited (record-less) name.
    if (newHandle !== undefined) bd.blockRecordHandle = newHandle;
    else delete bd.blockRecordHandle;
  }
  rebuildAuthoredMLeaderBlockContext(g, newHandle);
}

/**
 * SL-5 F3 (@codex round-4) — reconcile an AUTHORED block-content MLEADER's
 * CONTEXT_DATA with its freshly-resolved body 344.
 *
 * `buildAuthoredMLeaderContext` gates the context BlockData on a resolvable
 * `blockRecordHandle`, but an authored block-content MLEADER carries ONLY a model
 * `blockName` (no handle yet), so it emits `296 0` + no BlockData. The stamper
 * above then resolves the name → body 344 (`g.blockRecordHandle`), but WITHOUT
 * this the context still says "no block content" → the export ships
 * `contentType=1` + a body 344 whose CONTEXT_DATA header disagrees (hasBlock=0).
 *
 * When the name resolved (`newHandle` defined), the entity is block content
 * (`contentType===1`) and the PRIMARY context has no BlockData yet, REBUILD a
 * COMPLETE ezdxf `BlockData` (341 block_record + 14/24/34 extrusion + 15/25/35
 * insert + 16/26/36 scale + 46 rotation) and flip `hasBlock` on, so the entity
 * 344 and the context BlockData are consistent (both present). An UNRESOLVED name
 * leaves `296 0` + no 344 (the stamper set no body handle) — also consistent.
 * The scale/rotation come from the transport geometry (defaults 1 / 0); the
 * insert mirrors the context base point, exactly as buildAuthoredMLeaderContext
 * pairs them. A context that ALREADY carries a BlockData (imported / edited) is
 * left to the loop above — this only fills the authored `296 0` gap.
 */
function rebuildAuthoredMLeaderBlockContext(
  g: DxfMLeaderEntity["geometry"],
  newHandle: string | undefined,
): void {
  if (
    newHandle === undefined ||
    g.contentType !== 1 ||
    !g.contextData ||
    g.contextData.blockData
  ) {
    return;
  }
  const insert = g.contextData.basePoint ?? { x: 0, y: 0, z: 0 };
  const scaleValue = g.blockScale ?? 1;
  g.contextData.hasBlock = true;
  g.contextData.blockData = {
    blockRecordHandle: newHandle,
    extrusion: { x: 0, y: 0, z: 1 },
    insert: { x: insert.x, y: insert.y, z: insert.z },
    scale: { x: scaleValue, y: scaleValue, z: scaleValue },
    rotation: g.blockRotation ?? 0,
  };
}

/**
 * SL-17 — the minimum HEADER variable set for a valid, AutoCAD/ezdxf-readable
 * **R2018 (AC1032)** DXF, emitted (in this order, `$ACADVER` first) by the
 * headerless-fallback below when a from-scratch / in-memory doc carries no raw
 * HEADER yet must persist sysvars. `$ACADVER = AC1032` also drives the writer's
 * version-derived raw-UTF-8 text encoding (`shouldEscapeForVersion`). Mirrors
 * the values in the conformant blank R2018 skeleton (ezdxf `new("R2018")`):
 * codepage ANSI_1252, unset extents (`1e+20` / `-1e+20`), `$INSUNITS = 6`
 * (Meters, the skeleton value), `$MEASUREMENT = 1` (metric). `$HANDSEED`
 * carries a `0` placeholder that `patchHeaderHandSeedToMax` raises above the
 * real max handle.
 */
const R2018_MINIMAL_HEADER_VARS: ReadonlyArray<{ code: number; value: string }> =
  [
    { code: 9, value: "$ACADVER" },
    { code: 1, value: "AC1032" },
    { code: 9, value: "$ACADMAINTVER" },
    // SL-17 (@codex P2 #423) — `$ACADMAINTVER` is a 32-bit int in R2018
    // (AC1032): ezdxf `new("R2018")` and the conformant blank skeleton both
    // encode it with group code 90 (int32), NOT 70 (int16). Emitting 70 under
    // an advertised `$ACADVER = AC1032` produces a non-matching R2018 header.
    { code: 90, value: "4" },
    { code: 9, value: "$DWGCODEPAGE" },
    { code: 3, value: "ANSI_1252" },
    { code: 9, value: "$INSBASE" },
    { code: 10, value: "0.0" },
    { code: 20, value: "0.0" },
    { code: 30, value: "0.0" },
    { code: 9, value: "$EXTMIN" },
    { code: 10, value: "1e+20" },
    { code: 20, value: "1e+20" },
    { code: 30, value: "1e+20" },
    { code: 9, value: "$EXTMAX" },
    { code: 10, value: "-1e+20" },
    { code: 20, value: "-1e+20" },
    { code: 30, value: "-1e+20" },
    { code: 9, value: "$INSUNITS" },
    { code: 70, value: "6" },
    { code: 9, value: "$MEASUREMENT" },
    { code: 70, value: "1" },
    { code: 9, value: "$HANDSEED" },
    { code: 5, value: "0" },
  ];

export function exportDrawingDocumentToDxfDocument(
  inputDocument: DrawingDocument,
  options: ExportDrawingDocumentToDxfStringOptions = {},
): DxfDocument {
  const { dynVars, entitiesSnapshot, ensureConformant, carriedObjectRecords } =
    options;
  // DXF-CONFORMANT-SKELETON — a FROM-SCRATCH document (File → New, never
  // imported) carries none of the structural raw sections the carry-forward
  // writer re-emits (HEADER / CLASSES / VPORT / APPID / BLOCK_RECORD / BLOCKS /
  // OBJECTS root DICTIONARY) and its entity handles are non-hex ids. Back-fill
  // the conformant blank skeleton onto it (re-minting handles to hex, wiring
  // owner BLOCK_RECORD handles) so its export is AutoCAD/ezdxf-readable.
  // Gated on the opt-in `ensureConformant` flag (default OFF) so the in-memory
  // round-trip fixture tests stay byte-stable; the genuine SAVE paths pass it.
  // Imported documents are returned UNCHANGED, preserving byte-faithful
  // carry-forward. Runs FIRST so every downstream assembly + the
  // `collectAllEmittedHandles` seed see the skeleton-backed document.
  // Codex round-6 P1 — a STREAMED / cache-backed document keeps its entities in
  // `entitiesSnapshot`, with EMPTY inline `spaces[].entities`. `ensureConformant-
  // Skeleton` only re-mints the INLINE arrays, so for a from-scratch/INSERT host
  // that streamed its geometry the remint would produce an empty document and the
  // snapshot would then be dropped (`reminted` branch below) → an empty/partial
  // ENTITIES section on save = data loss. Materialize the snapshot INTO the input
  // spaces BEFORE the remint so the skeleton re-mints the REAL (streamed) entities
  // (hex ids + rewired owner/ref handles); the snapshot is then safely redundant.
  // Gated on `ensureConformant && entitiesSnapshot` — the non-conformant + the
  // no-snapshot paths are byte-identical to before.
  // T9b review round 4 — the compact-cache snapshot is materialised into the
  // spaces for EVERY path (not only the conformant one): the imported-document
  // preparation below rewrites `space.entities`, so it must see the real
  // entity arrays even when `ensureConformant` is off.
  const skeletonInput =
    entitiesSnapshot
      ? {
          ...inputDocument,
          spaces: inputDocument.spaces.map((space) => {
            const snap = entitiesSnapshot.get(space.id);
            return snap !== undefined ? { ...space, entities: snap } : space;
          }),
        }
      : inputDocument;
  const skeletonDocument = ensureConformant
    ? ensureConformantSkeleton(skeletonInput, carriedObjectRecords)
    : skeletonInput;
  // Codex P2 #1 — when the conformant-skeleton path RE-MINTED the document
  // (from-scratch: it returns a fresh CLONE with new hex `entity.id`s + rewired
  // owner/ref handles), the caller's `entitiesSnapshot` still holds the ORIGINAL
  // entity objects (non-hex ids, un-rewritten owner handles). Every downstream
  // consumer below prefers `entitiesSnapshot.get(space.id) ?? space.entities`,
  // so an un-dropped snapshot would emit the ORIGINAL `line-1` ids + miss the
  // owner/ref rewrites → defeats the conformant save (strict ezdxf rejects the
  // non-hex handle). The reminted `document.spaces[].entities` ARE the source of
  // truth, so DROP the snapshot for the reminted case. Imported docs are
  // returned BY REFERENCE (=== skeletonInput) → `reminted` is false → their
  // streamed/cache-backed snapshot is kept verbatim (UNAFFECTED, byte-faithful).
  // Compared against `skeletonInput` (NOT inputDocument) so the snapshot-
  // materialisation clone above does not falsely read as a remint for an imported
  // doc — only an actual ensureConformantSkeleton re-mint flips this true.
  const reminted = ensureConformant && skeletonDocument !== skeletonInput;
  // round-16 BUG 2 — the ATTDEF de-dup imported-source flag, evaluated on the
  // ORIGINAL pre-skeleton input so a from-scratch doc the skeleton REMINTED (which
  // adopts the skeleton's raw SECTIONS, making the POST-skeleton `document` falsely
  // read as imported) is correctly classified from-scratch (keep the reminted
  // typed ATTDEF, the 1005 ref target). When the skeleton ran (`ensureConformant`),
  // use the SAME `isImportedSourceDocument` predicate it early-returned on — so a
  // marker-dropped imported-source doc (raw sections, no marker) that passed
  // through keeps its byte-faithful raw ATTDEF (the BUG 2 fix). When the skeleton
  // did NOT run (`ensureConformant: false`), the raw-section signal is unreliable
  // (a from-scratch doc ALREADY run through the skeleton carries raw sections + no
  // marker yet its typed ATTDEFs are the ref targets), so fall back to the
  // AUTHORITATIVE `provenance` marker alone — a genuine imported pass-through has
  // it, a pre-skeletonized from-scratch doc does not.
  const importedSourceForDedup = ensureConformant
    ? isImportedSourceDocument(inputDocument)
    : inputDocument.provenance === "imported";
  // T9b review round 2/4 — an IMPORTED document bypasses the skeleton remint,
  // so every record / entity the user CREATED in it (a cloned DIMSTYLE, a new
  // LAYER / STYLE / LTYPE / BLOCK / LAYOUT, an authored `line-1` entity, an
  // INSERT with attributes) reaches the writer without a hex handle / owner →
  // ezdxf silently minted it, strict readers reject `5 line-1`. The typed
  // preparation pass mints + wires them on a DERIVED document (the caller's
  // document is untouched; pre-existing imported handles stay verbatim). It
  // rewrites `space.entities`, so the caller's snapshot is dropped like the
  // reminted case (the prepared spaces ARE the source of truth then).
  const document = importedSourceForDedup
    ? prepareImportedDocumentForExport(skeletonDocument, {
        referencedLinetypeNames: collectReferencedLinetypeNames(skeletonDocument),
      })
    : skeletonDocument;
  const effectiveSnapshot =
    reminted || document !== skeletonDocument ? undefined : entitiesSnapshot;
  // DYN-1.9: block-test spaces are transient sandbox canvases for
  // previewing dynamic blocks — they must never reach the emitted DXF.
  // block-edit spaces already carry empty entity arrays (their
  // authoring state lives in the target BlockDefinition), so skipping
  // them here is belt-and-suspenders safety for future refactors.
  //
  // Phase 4 Wave 1 Task C — prefer `entitiesSnapshot[space.id]` over
  // `space.entities` when the caller pre-snapshotted the cache. Falls
  // back to the legacy direct iteration so existing call sites (tests,
  // small-drawing paths) need no changes.
  // #314 redesign — EXTRA paper tabs (every layout space EXCEPT the first/
  // active one, which is backed by the un-suffixed `*Paper_Space` block) are
  // emitted ONCE into their `*Paper_Space<N>` BLOCK definition (see
  // `buildBlocksSectionFromDrawingBlocks`), NOT into the ENTITIES section. The
  // canonical AutoCAD R2000+ structure puts only `*Model_Space` content + the
  // FIRST/active paper space (un-suffixed `*Paper_Space`, group 67) in ENTITIES;
  // dumping the extra tabs here too re-homes every tab's content onto the active
  // paperspace from AutoCAD/ezdxf's view (the ENTITIES section is owned by the
  // active paperspace block_record) → lost-content / double-emit on re-import.
  // The plan is reused by the BLOCKS builder so the two sites stay symmetric.
  const paperPlan = buildPaperSpaceLayoutPlan(document);
  const supportedEntities = document.spaces
    .filter(
      (space) =>
        space.kind !== "block-test" &&
        space.kind !== "block-edit" &&
        // Exclude EXTRA-tab layout spaces (index >= 1). Model AND the first/
        // active paper space still emit to ENTITIES (the first paper keeps its
        // group 67 via `toDxfEntity(entity, space)`, unchanged behaviour).
        !paperPlan.excludedSpaceIds.has(space.id)
    )
    .flatMap((space) => {
      const sourceEntities = effectiveSnapshot?.get(space.id) ?? space.entities;
      // FIX — on the conformant from-scratch export (`reminted`), force an
      // explicit `8/<layer>` on every entity (user decision: "write code 8 on
      // every entity"). The carry-forward / imported path (`reminted` false)
      // omits it for the default layer "0", preserving byte-identical round-trip.
      return sourceEntities.map((entity) =>
        // round-4 F1: thread blocks. EXPORT-VP-FROZEN: thread the LAYER table (frozenLayers NAMES → native 331).
        toDxfEntity(entity, space, { forceLayerEmit: reminted, blocks: document.blocks, layers: document.styleTables?.layers ?? [] }),
      );
    });

  // E2 fidelity — append top-level unsupported entity groups verbatim
  // after the supported ones so the writer emits them inside the ENTITIES
  // section before ENDSEC. Each RawEntityGroup is converted back to a
  // DxfUnsupportedEntity record whose `.groups[]` starts with the entity
  // type header; the writer's `serializeEntity` branch for unsupported
  // returns those groups directly, preserving every byte of the original
  // DXF representation.
  //
  // FINDING 2 (T9 round-2) — on the conformant / reminted from-scratch path,
  // thread `reminted` so a raw unsupported entity that carried NO explicit layer
  // gets a `8/0` group injected, honouring the "code 8 on every entity"
  // invariant the typed `toDxfEntity({ forceLayerEmit: reminted })` path above
  // enforces. Off the conformant path (`reminted` false) the raw bytes stay
  // byte-identical (no injection).
  const entities: DxfEntity[] = [...supportedEntities];
  for (const raw of document.rawEntityGroups ?? []) {
    entities.push(rawEntityGroupToDxfUnsupported(raw, reminted));
  }

  const sections: DxfSection[] = [];

  // E2 fidelity — re-emit HEADER / CLASSES / ACDSDATA / THUMBNAILIMAGE
  // raw sections that were preserved at import time. They sit before
  // TABLES in AutoCAD-authored DXFs, so we push them first.
  //
  // Phase 4 DYN* persistence: when `dynVars` is provided, patch the HEADER
  // section's group list so $DYNMODE / $DYNDIGRIP / … are written with the
  // current sysvar values. `patchDynVarsIntoHeaderGroups` replaces existing
  // entries in-situ and appends any that were absent, preserving every other
  // header variable verbatim (AutoCAD parity).
  //
  // PDF-6.1 linetype-scale persistence (Codex P2 #189): the typed
  // `doc.ltscale/celtscale/psltscale/msltscale` fields are the source of truth
  // for $LTSCALE/$CELTSCALE/$PSLTSCALE/$MSLTSCALE. Before this fix the export
  // re-emitted the OLD raw HEADER and never patched these back, so values
  // edited after import (or a doc built with no raw HEADER) silently dropped /
  // reverted to the previous header bytes. Patch them in the same way as the
  // DYN vars so they always round-trip.
  const hasDynVars = dynVars != null && Object.keys(dynVars).length > 0;
  const linetypeVars = collectLinetypeVars(document);
  const hasLinetypeVars = Object.keys(linetypeVars).length > 0;
  // Batch-6 — document-level sysvars ($INSUNITS / $PDMODE / $PDSIZE).
  const documentVars = collectDocumentVars(document);
  const hasDocumentVars = Object.keys(documentVars).length > 0;
  // BASE — point sysvar $INSBASE (drawing insertion base point).
  const pointVars = collectPointVars(document);
  const hasPointVars = Object.keys(pointVars).length > 0;
  // OSNAP-HEADER-ROUNDTRIP — running object-snap sysvar ($OSMODE).
  const snapVars = collectSnapVars(document);
  const hasSnapVars = Object.keys(snapVars).length > 0;
  for (const raw of document.rawPassthroughSections ?? []) {
    let groups = raw.groups.map((g) => ({ code: g.code, value: g.value }));
    if (raw.name === "HEADER") {
      if (hasDynVars) groups = patchDynVarsIntoHeaderGroups(groups, dynVars!);
      if (hasLinetypeVars) {
        groups = patchLinetypeVarsIntoHeaderGroups(groups, linetypeVars);
      }
      if (hasDocumentVars) {
        groups = patchDocumentVarsIntoHeaderGroups(groups, documentVars);
      }
      if (hasPointVars) {
        groups = patchPointVarsIntoHeaderGroups(groups, pointVars);
      }
      if (hasSnapVars) {
        groups = patchSnapVarsIntoHeaderGroups(groups, snapVars);
      }
    }
    sections.push({
      kind: "raw-section" as const,
      name: raw.name,
      groups,
    });
  }
  // If no HEADER raw section existed at import (e.g. minimal DXF files, or a
  // doc assembled in memory) but DYN / linetype sysvars need persisting,
  // inject a minimal HEADER section so the vars are not lost. AutoCAD always
  // expects a HEADER section in a valid DXF file.
  //
  // SL-17 — seed the R2018/AC1032 minimum HEADER vars FIRST (see
  // `R2018_MINIMAL_HEADER_VARS`) so a from-scratch / headerless doc that
  // reaches this fallback still yields a valid R2018 file: `$ACADVER = AC1032`
  // (also drives the writer's raw-UTF-8 encoding default), `$ACADMAINTVER`,
  // `$DWGCODEPAGE`, `$INSBASE`, `$EXTMIN`/`$EXTMAX`, `$INSUNITS`,
  // `$MEASUREMENT` and a `$HANDSEED` placeholder that `patchHeaderHandSeedToMax`
  // (below) bumps above the real handle maximum.
  if (
    (hasDynVars ||
      hasLinetypeVars ||
      hasDocumentVars ||
      hasPointVars ||
      hasSnapVars) &&
    !(document.rawPassthroughSections ?? []).some((s) => s.name === "HEADER")
  ) {
    // SL-17 P2 — ONLY a from-scratch (never-imported) doc adopts the R2018/AC1032
    // minimal-HEADER seed. An imported header-less source (ENTITIES-only DXF —
    // provenance marker OR imported raw SECTION) keeps its carried-forward
    // version: seeding `$ACADVER = AC1032` here would (a) leak an R2018 version
    // bump onto an UNEDITED re-export AND (b) flip its escaped-Cyrillic TEXT to
    // raw UTF-8 (the writer's escape default is `$ACADVER`-derived — NO $ACADVER
    // ⇒ legacy `\U+XXXX` escape). Guard on the SAME `isImportedSourceDocument`
    // predicate the skeleton early-returns on, evaluated on the ORIGINAL
    // pre-skeleton `inputDocument` (matching the ATTDEF-dedup site above).
    // SL-17 P3 — route the seed through the SAME patch functions the raw-HEADER-
    // present path (above) uses, instead of a blind prepend. An overlapping seed
    // var ($INSUNITS ← documentVars, $INSBASE ← pointVars) is then OVERWRITTEN
    // IN-PLACE with the document's real value rather than emitted twice (the
    // hardcoded 6 then the real value) → no duplicate / reader-precedence-
    // dependent conflicting var. Non-overlapping vars ($DYN*/$LTSCALE*/$OSMODE/
    // $PDMODE/…) append exactly as before; on the imported (empty-seed) path each
    // patch degenerates to a plain append, identical to the old write path.
    let headerGroups: DxfGroup[] = isImportedSourceDocument(inputDocument)
      ? []
      : R2018_MINIMAL_HEADER_VARS.map((g) => ({ ...g }));
    if (hasDynVars) {
      headerGroups = patchDynVarsIntoHeaderGroups(headerGroups, dynVars!);
    }
    if (hasLinetypeVars) {
      headerGroups = patchLinetypeVarsIntoHeaderGroups(
        headerGroups,
        linetypeVars,
      );
    }
    if (hasDocumentVars) {
      headerGroups = patchDocumentVarsIntoHeaderGroups(
        headerGroups,
        documentVars,
      );
    }
    if (hasPointVars) {
      headerGroups = patchPointVarsIntoHeaderGroups(headerGroups, pointVars);
    }
    if (hasSnapVars) {
      headerGroups = patchSnapVarsIntoHeaderGroups(headerGroups, snapVars);
    }
    sections.unshift({
      kind: "raw-section" as const,
      name: "HEADER",
      groups: headerGroups,
    });
  }

  // Insert TABLES section before ENTITIES if we have style tables. The
  // BLOCK_RECORD table piggybacks here because it lives inside TABLES.
  // E2 fidelity — thread `rawTableGroups` captured at import through the
  // synthetic `existingTablesSection` so unknown TABLES-section records
  // (VPORT / VIEW / UCS / APPID / …) round-trip verbatim.
  const blockRecordEntries = exportBlockRecords(document.blockRecords);
  const syntheticTablesSection: DxfTablesSection | null =
    (document.rawTableGroups ?? []).length > 0
      ? {
          kind: "tables-section" as const,
          textStyles: [],
          dimensionStyles: [],
          layers: [],
          blockRecords: [],
          rawTableGroups: (document.rawTableGroups ?? []).map((g) => ({
            code: g.code,
            value: g.value,
          })),
        }
      : null;
  // FIX-4 — the set of linetype names the drawing actually references, so the
  // LTYPE export filter keeps USED synthetic-seed linetypes while dropping the
  // unused standard definitions on a plain round-trip.
  // FIX-1 — pass `entitiesSnapshot` so the ref collector scans the SAME effective
  // per-space entity source the ENTITIES serializer used above (a snapshot-only
  // linetype ref would otherwise be dropped → dangling group-6 reference).
  const referencedLinetypeNames = collectReferencedLinetypeNames(
    document,
    effectiveSnapshot,
  );
  const tablesSection = exportStyleTables(
    document.styleTables,
    syntheticTablesSection,
    blockRecordEntries,
    document.linetypes,
    referencedLinetypeNames,
    document.linetypeTableHeader,
    document.symbolTableHeaders,
  );

  // E2 fidelity — build a BLOCKS section from `document.blocks`. Before
  // this change the export path produced no BLOCKS section at all; the
  // writer only injected a stub with anonymous dimension blocks. That
  // lost every BLOCK definition on round-trip (including both supported
  // children like LINE/TEXT and raw unsupported children like ACAD_TABLE
  // /SPLINE/XRECORD). This walk emits each block's supported children
  // via `toDxfEntity` (skipping Attdef because its raw counterpart in
  // `rawEntityGroups` re-emits it verbatim — avoids double emission)
  // plus every RawEntityGroup record as a DxfUnsupportedEntity.
  // round-16 BUG 2 — pass the imported-source flag computed above (on the ORIGINAL
  // pre-skeleton input + the `ensureConformant` gate) so the ATTDEF de-dup branch
  // agrees with the conformant-skeleton pass-through decision:
  //   imported-source ⇒ skeleton passed through ⇒ keep byte-faithful raw ATTDEF;
  //   from-scratch    ⇒ skeleton reminted       ⇒ keep reminted typed ATTDEF.
  // F1 (#314 round-2) — thread the SAME `effectiveSnapshot` the ENTITIES loop
  // iterates so the EXTRA-tab paper-block rebuild reads the relocated geometry
  // from the compact-cache snapshot (the inline `space.entities` arrays are empty
  // on the streaming path); without it the extra tabs' geometry is dropped.
  let blocksSection = buildBlocksSectionFromDrawingBlocks(
    document,
    importedSourceForDedup,
    paperPlan,
    effectiveSnapshot,
    // FIX — force an explicit `8/<layer>` on every block-definition child on the
    // conformant from-scratch path (`reminted`), matching the ENTITIES loop.
    reminted,
  );

  // OBJECTS section — layouts + dynamic block authoring objects.
  // MLEADERSTYLE objects are emitted by the writer stub when the TABLES
  // section has MLEADERSTYLE entries; layouts are authored here so
  // paper-space tabs round-trip. Dynamic block metadata (DYN-1+) is
  // serialized via `emitDynamicBlockObjects` for every block definition
  // that carries a `dynamicMetadata` payload.
  //
  // E2 fidelity — thread `rawObjectGroups` captured at import so every
  // non-structured OBJECTS record (MATERIAL / VISUALSTYLE / XRECORD /
  // SCALE / PLOTSETTINGS / GROUP / …) round-trips verbatim. Real-world
  // DXFs carry 500k+ such groups.
  const layoutEntries = exportLayouts(document.layouts);
  const passthroughObjectGroups = (document.rawObjectGroups ?? []).map((g) => ({
    code: g.code,
    value: g.value,
  }));

  // SL-6 (R2018 `cross-block-handle-collision` P1) — synthesize the dynamic-block
  // OBJECTS graph from ONE shared, monotonic, collision-checked handle minter
  // seeded ABOVE the document's max emitted handle. The per-block writer used to
  // restart at 0x100000 for every block, so a document with ≥2 dynamic blocks
  // emitted DUPLICATE group-5 handles → ezdxf's EntityDB overwrote the first
  // record + strict `readfile` rejected the non-unique handle. We collect the
  // document-wide handle population FIRST (every built section EXCEPT the not-yet-
  // synthesized dynamic objects), then mint every dynamic object's handle from a
  // `makeHandleMinter` seeded with that population (the same one-truth minter the
  // annotative / MLEADER paths use). The `scanObjectsSection` object is reused for
  // the full `emittedHandles` scan below — its `dynamicBlockObjects` field is
  // populated in place once the objects are minted — so the section list is built
  // exactly once.
  const scanObjectsSection: DxfObjectsSection = {
    kind: "objects-section" as const,
    dictionaries: [],
    multileaderStyles: [],
    layouts: layoutEntries,
    dynamicBlockObjects: [],
    rawObjectGroups: passthroughObjectGroups,
  };
  const handleScanSections: DxfSection[] = [
    tablesSection,
    ...(blocksSection ? [blocksSection] : []),
    { kind: "entities-section" as const, entities },
    scanObjectsSection,
  ];
  const preDynamicHandles = collectAllEmittedHandles(handleScanSections);
  collectRetainedDynamicBlockHandles(document.blocks, preDynamicHandles);
  const dynamicBlockObjects = exportDynamicBlockObjects(
    document.blocks,
    makeHandleMinter(
      preDynamicHandles,
      // Seed above BOTH the document max (now INCLUDING the retained dynamic
      // handles) AND the 0x100000 dynamic-object floor the writer/MLEADER paths
      // conventionally use, so a small-handle document never mints in the low
      // structural-handle range — nor at/near a retained id like 0x100001.
      Math.max(0x100000, maxHexHandleValue(preDynamicHandles)),
    ),
  );
  scanObjectsSection.dynamicBlockObjects = dynamicBlockObjects;

  // Resolve MLEADER text-style names → STYLE handles (group 340). Runs
  // after EVERY handle-bearing structure is built (entities, tables,
  // blocks, OBJECTS, dynamic blocks, layouts) so the synthetic-handle
  // minter (Codex #243 F1) can collision-avoid against the document-wide
  // handle population — not just STYLE records. The dynamic-block writer
  // mints from the SAME 0x100000 range, so a STYLE-only seed could mint a
  // duplicate group-5 → ambiguous group-340 on re-import. We seed from all
  // built structures, then stamp BEFORE the section push decisions so a
  // freshly-minted STYLE record is still counted when deciding whether the
  // TABLES section is non-empty.
  //
  // Model/paper-space `entities` AND every block-definition child are stamped
  // in ONE pass (#249 follow-up — block-child MLEADER stamping, now closed).
  // As of Batch-7 (S-BLOCKCHILD-MLEADER) a MLEADER CAN be a block-definition
  // child (blockChildEntitySchema includes it), and `toDxfEntity` projects its
  // `textStyleName` onto the transport exactly like a model-space one. The
  // writer emits the in-context group 340 ONLY from a HANDLE
  // (mleaderContextWriter: geom.textStyleHandle / mTextData.styleHandle —
  // never from the name), so a block-child MLEADER with a named text style
  // used to export WITHOUT its 340 → the style binding fell back to Standard
  // on re-import (round-trip data loss). Walking `blocksSection.blocks[]`
  // children through the SAME stamper gives full parity: the flat
  // `textStyleHandle` AND the in-context `mTextData.styleHandle` (FIX 4) both
  // get the resolved STYLE handle, and minting stays document-wide-unique
  // because one call shares one `usedHandles` set across all lists. The
  // per-annotative-scale model snapshots (`annotativeContexts`, B2) carry NO
  // style-handle field at the model level — for model-space and block-child
  // MLEADERs alike — so there is nothing extra to stamp for annotative
  // contexts; the single structural CONTEXT_DATA's MTextData is the only
  // in-context 340 home on the bridge-export path.
  // SL-6 — re-scan the SAME section list now that `scanObjectsSection`
  // carries the (uniquely-minted) dynamic objects, so the MLEADER / annotative
  // minters seeded below collision-avoid against the dynamic-block handles too.
  const emittedHandles = collectAllEmittedHandles(handleScanSections);
  // Concatenate top-level entities with all block-definition children; the
  // DxfEntity records are shared references, so stamping through the combined
  // list mutates the SAME objects the BLOCKS serializer will emit.
  const blockChildEntities: DxfEntity[] =
    blocksSection?.blocks.flatMap((block) => block.entities) ?? [];
  stampMLeaderTextStyleHandles(
    [...entities, ...blockChildEntities],
    tablesSection.textStyles,
    emittedHandles,
  );
  // SL-5 F4 (@codex round-2) — `stampMLeaderTextStyleHandles` can MINT a fresh
  // STYLE record handle into `tablesSection.textStyles` (for a named-but-
  // absent text style), but that handle was never folded back into
  // `emittedHandles` — so the MLEADERSTYLE-object minter below (seeded from
  // `emittedHandles`, floor `maxHexHandleValue`) could pick the SAME hex in a
  // dense drawing → duplicate object handles. Re-scan the STYLE table now so the
  // minter (and its floor) collision-avoid against every freshly-minted STYLE
  // handle too.
  for (const style of tablesSection.textStyles) {
    if (style.handle) emittedHandles.add(style.handle.toLowerCase());
  }
  // SL-5 F2 (@codex round-7) — mint a handle for EVERY referenced-but-handle-less
  // MLEADERSTYLE ref record (340 linetype / 341 arrow / 342 style / 343 block; a
  // 342 must resolve → a missing STYLE is created), BEFORE the resolution maps, and
  // fold each into `emittedHandles`. #432 r13 (@codex R13-2) — a CUSTOMIZED/EDITED
  // Standard is emitted as an object (isDefaultStandardStyle, shared with the
  // builder), so its refs are stamped here too or it reopens with defaults.
  const standardStyle = (document.styleTables?.multileaderStyles ?? []).find(
    (s) => s.name.toLowerCase() === "standard",
  );
  const emitStandard = standardStyle !== undefined && !isDefaultStandardStyle(standardStyle);
  const referencedLeaderStyleNames = new Set<string>();
  for (const e of [...entities, ...blockChildEntities]) {
    if (e.kind === "supported-entity" && e.type === "MLEADER") {
      const n = (e.geometry.styleName ?? "Standard").toLowerCase();
      if (n !== "standard" || emitStandard) referencedLeaderStyleNames.add(n);
    }
  }
  stampMLeaderStyleReferencedRecordHandles(
    referencedLeaderStyleNames,
    document.styleTables?.multileaderStyles ?? [],
    tablesSection.textStyles,
    tablesSection.linetypes,
    blockRecordEntries,
    emittedHandles,
    Math.max(0x120000, maxHexHandleValue(emittedHandles)),
  );
  // SL-5 F2 (@codex round-2) — build the document's emitted TEXTSTYLE / LTYPE /
  // BLOCK_RECORD name→handle maps so a referenced MLEADERSTYLE's NAME references
  // (textStyleName / leaderLineType / arrowheadBlock / blockName) resolve to
  // their real 340-343 handle fields (custom refs no longer fall back to
  // defaults on reopen). Keyed by lowercased name (DXF names are
  // case-insensitive), only entries carrying a real handle.
  const styleHandleByName = new Map<string, string>();
  for (const style of tablesSection.textStyles) {
    if (style.handle) styleHandleByName.set(style.name.toLowerCase(), style.handle);
  }
  const lineTypeHandleByName = new Map<string, string>();
  for (const lt of tablesSection.linetypes ?? []) {
    if (lt.handle) lineTypeHandleByName.set(lt.name.toLowerCase(), lt.handle);
  }
  const blockHandleByName = new Map<string, string>();
  for (const br of blockRecordEntries) {
    if (br.handle) blockHandleByName.set(br.name.toLowerCase(), br.handle);
  }
  // SL-5 P2-1 — emit an MLEADERSTYLE object for every NON-Standard leader style
  // an MLEADER references so the entity's body 340 resolves to a REAL object
  // (save/reopen keeps the chosen style instead of falling back to Standard).
  // Discover the ACAD_MLEADERSTYLE dictionary handle first so each object is
  // owned by (and, below, listed under) it. Runs AFTER the document-wide handle
  // scan so the minted object handles collide with nothing; the freshly minted
  // handles are then FOLDED INTO `emittedHandles` so the annotative graph (and
  // the $HANDSEED repatch) mint above them.
  const mleaderStyleDictHandle = findObjectDictionaryHandle(
    passthroughObjectGroups,
    "ACAD_MLEADERSTYLE",
  );
  // SL-5 F1 (@codex round-4) — the surviving OBJECTS-record group-5 handles a
  // reused source handle must not collide with (the ACAD_MLEADERSTYLE dictionary
  // handle among them). MLEADERSTYLE objects are consumed to the typed model, so
  // this never contains a rebuilt style's own handle — see the helper doc.
  const foreignObjectRecordHandles = collectForeignObjectRecordHandles(
    passthroughObjectGroups,
  );
  // SL-5 F3 (@codex round-6) — the document-wide OWN (group-5-class) handle set a
  // reused MLEADERSTYLE source handle must not alias (see `collectOwnHandlesDeep`).
  const documentOwnHandles = new Set<string>();
  for (const section of handleScanSections) collectOwnHandlesDeep(section, documentOwnHandles);
  const mleaderStyleObjects = buildReferencedMLeaderStyleObjects(
    document,
    [...entities, ...blockChildEntities],
    mleaderStyleDictHandle,
    emittedHandles,
    // Mint above the document-wide max (and ≥0x130000, disjoint from the
    // text-style/synthetic-leader stamp bands) — the single maxHexHandleValue
    // definition lives here in the orchestrator. `emittedHandles` now includes
    // the freshly-minted STYLE handles (F4), so the floor accounts for them.
    Math.max(0x130000, maxHexHandleValue(emittedHandles)),
    foreignObjectRecordHandles,
    documentOwnHandles,
    { textStyleHandleByName: styleHandleByName, lineTypeHandleByName, blockHandleByName },
  );
  for (const s of mleaderStyleObjects.entries) {
    if (s.handle) emittedHandles.add(s.handle.toLowerCase());
  }
  // Populate the handle-scan section so the SL-6 re-scan / $HANDSEED census that
  // read `scanObjectsSection` count the MLEADERSTYLE object handles too.
  scanObjectsSection.multileaderStyles = mleaderStyleObjects.entries;
  // SL-5 P2 / P2-3 — resolve the AcDbMLeader body 340 (leader-style handle):
  // styleName → the emitted object's handle (single source of truth) for a
  // non-Standard style; Standard/no-object entities keep their source handle
  // (imported) or a synthetic hex (authored). Shares the document-wide handle
  // population so any minted synthetic 340 is collision-free.
  stampMLeaderLeaderStyleHandles(
    [...entities, ...blockChildEntities],
    mleaderStyleObjects.handleByName,
    emittedHandles,
  );
  // Re-resolve MLEADER block-content (body 344) handles against the document's
  // BLOCK_RECORD population so an EDITED content block exports the new block's
  // handle instead of the stale source handle (Codex #285-8). Unchanged /
  // orphan references are left verbatim. `blockRecordEntries` is the same
  // name↔handle set the writer emits for the BLOCK_RECORD table.
  //
  // Walk top-level entities AND block-definition children in ONE pass (#294
  // Codex P2, block-child arm): `buildBlocksSectionFromDrawingBlocks` above
  // converts `block.entities` — including Batch-7 block-child Multileaders —
  // through the same `toDxfEntity`, so a block-child MLEADER carries the same
  // verbatim source 344 and loses a blockName edit identically if only the
  // top-level array is refreshed (mirrors the F1 ext-dict stamp below, which
  // already walks `blocksSection.blocks[].entities`).
  const blockHandleStampTargets = [...entities, ...(blocksSection?.blocks ?? []).flatMap((b) => b.entities)];
  stampMLeaderBlockContentHandles(blockHandleStampTargets, blockRecordEntries);
  // SL-11 round-8 F2 — MLEADER entity body-342 (arrowhead) authored NAME → its BLOCK_RECORD hex handle (writer emits 342 verbatim → a NAME dangles on reopen); imported live handles stay verbatim, unresolvable values drop. round-9 F1 default arrow → null handle `0`; round-9 F2 a handle-less/non-hex named arrow record gets a hex handle minted onto it (folded into emittedHandles so the DIMENSION stamp below re-seeds above it).
  stampMleaderEntityArrowheadHandles(blockHandleStampTargets, blockRecordEntries, emittedHandles, maxHexHandleValue(emittedHandles));
  // SL-11 — DIMENSION DSTYLE arrowhead overrides → BLOCK_RECORD handles (synthesizing an absent built-in arrow's `_<NAME>` record+BLOCK; may reassign blocksSection).
  blocksSection = stampDimensionDStyleArrowheadHandles(blockHandleStampTargets, blockRecordEntries, emittedHandles, maxHexHandleValue(emittedHandles), blocksSection);

  // S-INSERT-ANNOTATIVE — re-synthesize the OBJECTS annotative scale graph
  // (AcDbScale catalogue + ACAD_SCALELIST dict + per-scale context-data
  // records) from each annotative entity's typed `annotativeContexts`. The
  // bridge import resolves these per-entity but the export previously dropped
  // the OBJECTS-side graph entirely, so a resolved annotative INSERT / TEXT /
  // MTEXT / HATCH lost its contexts on round-trip. The dxf-io writer
  // auto-emits objectsSection.acadScales / annotScaleContexts /
  // acadScaleListDict when present (writer.ts), and resolveAnnotativeContexts
  // re-attaches them on re-import via the ext-dict ownerHandle anchor.
  // F2 — thread `entitiesSnapshot` so the annotative graph reads from the SAME
  // per-space source the ENTITIES serializer used above. An annotative entity
  // present ONLY in the snapshot (stale/empty `space.entities`) would otherwise
  // be written as an entity but get NO AcDbScale/context OBJECTS records.
  // F5' (#314 round-2) — thread the SAME paper-space relocation plan the BLOCKS
  // builder used so the annotative graph SKIPS the LAYOUT block defs whose
  // supported children are emitted via a space (model / first paper / paired
  // extra tab). Without it the importer's duplicate block-def copy of a relocated
  // annotative entity is scanned TOO → duplicate context records / orphaned owner
  // handle on re-import.
  const annotativeGraph = buildAnnotativeObjectsGraph(
    document,
    emittedHandles,
    effectiveSnapshot,
    paperPlan,
  );
  // Stamp minted extension-dict handles onto the emitted entity records (matched
  // by DXF handle = model id) so the FK chain re-resolves on re-import for
  // pure-model annotative entities that lacked an ext-dict handle.
  //
  // F1 — stamp BOTH top-level entities AND block-definition children. The
  // graph mints ext-dict handles for annotative entities living inside block
  // definitions too (buildAnnotativeObjectsGraph scans `document.blocks`), but
  // the old loop walked only top-level `entities` — so a block-child's minted
  // handle was never stamped onto its emitted DxfEntity record (inside
  // `blocksSection.blocks[].entities`) and its contexts vanished on re-import.
  // Block-child DxfEntity `common.handle` equals the model child's `id` (set by
  // `toDxfEntity`), so the same handle→minted lookup applies.
  if (annotativeGraph.mintedExtDictHandles.size > 0) {
    const stampEntity = (entity: DxfEntity): void => {
      if (entity.kind !== "supported-entity") return;
      const handle = entity.common.handle;
      if (!handle) return;
      const minted = annotativeGraph.mintedExtDictHandles.get(handle);
      if (minted && !entity.common.extensionDictionaryHandle) {
        entity.common.extensionDictionaryHandle = minted;
      }
    };
    for (const entity of entities) stampEntity(entity);
    for (const block of blocksSection?.blocks ?? []) {
      for (const child of block.entities) stampEntity(child);
    }
  }

  // F3 (single-source) — when the synthesized graph REUSED a preserved
  // ACAD_SCALELIST dictionary's handle, STRIP that now-stale preserved SCALELIST
  // container record from the raw OBJECTS passthrough so the output carries
  // exactly ONE ACAD_SCALELIST dict (the synthesized one, under the reused
  // handle the root dictionary still points at).
  let objectsRawGroups =
    annotativeGraph.reusedScaleListHandle !== undefined
      ? stripPreservedScaleListDict(
          passthroughObjectGroups,
          annotativeGraph.reusedScaleListHandle,
        )
      : passthroughObjectGroups;

  // N7 VPCLIP-BOUNDARY-EXPORT — synthesize the SPATIAL_FILTER + owning ext-dict
  // / ACAD_FILTER DICTIONARY chain for a NON-rectangular VPCLIP boundary
  // authored IN-APP (`applyViewportClipSetPolygon`'s explicit-points path, no
  // matched boundary object), so it survives export/reload instead of being
  // silently dropped. Stamps both top-level `entities` AND every block-emitted
  // EXTRA paper-tab viewport, and threads `effectiveSnapshot` so a snapshot-
  // only clipped viewport is still scanned (@codex P2 ×2 — see
  // `viewportClipObjectsSynthesize.ts` header for the full design rationale).
  const viewportClipGraph = applyViewportClipBoundarySynthesis({
    document,
    entities,
    blocksSectionBlocks: blocksSection?.blocks ?? [],
    objectsRawGroups,
    emittedHandles,
    // The annotative graph's internal minter is entirely PRIVATE and does not
    // write back into the shared `emittedHandles` Set — seed this module's
    // minter with the FULL set of handles it minted (every AcDbScale / context
    // / SCALELIST handle, not just the ext-dict ones) so it can never alias one
    // of them (@codex P1, N7 PR #556 round 3 — see `collectAnnotativeGraph
    // MintedHandles`'s doc comment for the exact collision this closes).
    extraSeedHandles: collectAnnotativeGraphMintedHandles(annotativeGraph),
    entitiesSnapshot: effectiveSnapshot,
  });
  objectsRawGroups = viewportClipGraph.rawObjectGroups;

  // SL-5 F3 (@codex round-7) — register the freshly-emitted MLEADERSTYLE objects
  // under the ACAD_MLEADERSTYLE dictionary so its `350` children ALWAYS match
  // EXACTLY the emitted objects. Fire whenever the dictionary EXISTS — INCLUDING
  // the empty-entries case: when the last non-Standard style becomes unreferenced,
  // `entries` is empty, and the round-6 `entries.length > 0` guard SKIPPED the
  // replace, leaving the preserved dict's stale `350` children pointing at handles
  // no longer serialized (the parser already pulled the MLEADERSTYLE records out of
  // the passthrough) → a dangling dict child. `replaceDictionaryChildEntries` with
  // an empty list CLEARS those stale children (keeping the dict record + its nested
  // 102 blocks). Dict lifecycle: children are UPDATED when the style set changes and
  // CLEARED when zero styles emit; the dict itself is created by the conformant
  // skeleton (which also sweeps its own dangling Standard child via FIX-202). A
  // Standard-only doc with no preserved ACAD_MLEADERSTYLE dict is untouched
  // (`mleaderStyleDictHandle` undefined → census byte-unchanged).
  if (mleaderStyleDictHandle) {
    objectsRawGroups = replaceDictionaryChildEntries(
      objectsRawGroups,
      mleaderStyleDictHandle,
      mleaderStyleObjects.entries.map((e) => ({
        name: e.name,
        handle: e.handle,
      })),
    );
  }

  if (
    tablesSection.textStyles.length > 0 ||
    tablesSection.dimensionStyles.length > 0 ||
    tablesSection.layers.length > 0 ||
    tablesSection.blockRecords.length > 0 ||
    (tablesSection.linetypes?.length ?? 0) > 0 ||
    tablesSection.rawTableGroups.length > 0
  ) {
    sections.push(tablesSection);
  }

  if (blocksSection) {
    sections.push(blocksSection);
  }

  sections.push({
    kind: "entities-section" as const,
    entities,
  });

  // #432 r10/r11 R10-3/R11-3 — synthesize the MLEADERSTYLE root/sub dict (see helper).
  const mleaderStyleDicts = maybeSynthesizeMLeaderStyleRootDicts(
    mleaderStyleObjects.entries, mleaderStyleDictHandle, objectsRawGroups,
    emittedHandles, Math.max(0x140000, maxHexHandleValue(emittedHandles)),
  );
  objectsRawGroups = mleaderStyleDicts.rawObjectGroups;

  // AW-1 write-back (docs/MASTERPLAN.md AW-1; full contract in
  // dxfBridge.arrayAssocExport.ts). Surgically patches every dirty
  // associative-array field into `objectsRawGroups` IN PLACE (existing
  // group positions only — never reorders the section, so the entity's own
  // ext-dict DICTIONARY stays exactly where AW-0 already put it).
  objectsRawGroups = applyAssocArrayWriteBack(
    document,
    objectsRawGroups,
    effectiveSnapshot,
  );

  if (
    layoutEntries.length > 0 ||
    dynamicBlockObjects.length > 0 ||
    objectsRawGroups.length > 0 ||
    mleaderStyleObjects.entries.length > 0 ||
    annotativeGraph.annotScaleContexts.length > 0 ||
    annotativeGraph.acadScales.length > 0 ||
    viewportClipGraph.dictionaries.length > 0 ||
    viewportClipGraph.spatialFilters.length > 0
  ) {
    const objectsSection: DxfObjectsSection = {
      kind: "objects-section" as const,
      // N7 — the synthesized VPCLIP ext-dict / ACAD_FILTER DICTIONARY records
      // (if any) ride alongside the MLEADERSTYLE root/sub dicts; both are typed
      // `DxfDictionaryEntry[]` the writer emits unconditionally ahead of the
      // raw passthrough (`serializeObjectsSection`).
      dictionaries: [...mleaderStyleDicts.dictionaries, ...viewportClipGraph.dictionaries],
      // SL-5 P2-1 — the emitted MLEADERSTYLE objects (non-Standard referenced
      // styles); the writer serializes each so the entity 340 resolves.
      multileaderStyles: mleaderStyleObjects.entries,
      layouts: layoutEntries,
      dynamicBlockObjects,
      // F3 — the SCALELIST-stripped raw passthrough (see objectsRawGroups).
      rawObjectGroups: objectsRawGroups,
    };
    if (annotativeGraph.acadScales.length > 0) {
      objectsSection.acadScales = annotativeGraph.acadScales;
    }
    if (annotativeGraph.annotScaleContexts.length > 0) {
      objectsSection.annotScaleContexts = annotativeGraph.annotScaleContexts;
    }
    if (annotativeGraph.acadScaleListDict) {
      objectsSection.acadScaleListDict = annotativeGraph.acadScaleListDict;
    }
    // N7 — new SPATIAL_FILTER records with no matching `rawObjectGroups`
    // sentinel (they are BRAND NEW, never parsed) are flushed by the writer's
    // existing "finding 7" fallback (`serializeObjectsSection`: "Flush any
    // typed SPATIAL_FILTER that no sentinel claimed") — no writer.ts change
    // needed; that path already exists for exactly this synthetic case.
    if (viewportClipGraph.spatialFilters.length > 0) {
      objectsSection.spatialFilters = viewportClipGraph.spatialFilters;
    }
    sections.push(objectsSection);
  }

  // ROOT D ($HANDSEED) — re-patch the HEADER `$HANDSEED` to the document-wide
  // emitted-handle maximum + 1, AFTER every minting pass has run (else a strict
  // ezdxf audit flags `$HANDSEED` < max handle, and AutoCAD re-issues a colliding
  // handle on the next edit). Collects over the FINAL assembled sections (stamped
  // entities + annotative + synthesized OBJECTS records). No-op when there is no
  // HEADER section (a bare in-memory round-trip with `ensureConformant` off).
  patchHeaderHandSeedToMax(sections);

  return { sections };
}

export function exportDrawingDocumentToDxfString(
  document: DrawingDocument,
  options: ExportDrawingDocumentToDxfStringOptions = {}
): string {
  return serializeDxfDocument(
    exportDrawingDocumentToDxfDocument(document, options),
    options,
  );
}

// #314 round-2 — re-export the BLOCKS-section builder + raw-group helper
// (extracted to `dxfBridge.blocksSection.ts` to keep this file under the
// 2000-line cap) so the public barrel surface and every existing importer of
// `rawEntityGroupToDxfUnsupported` / `buildBlocksSectionFromDrawingBlocks` from
// `dxfBridge.export` are unchanged.
export { buildBlocksSectionFromDrawingBlocks, rawEntityGroupToDxfUnsupported };
